import { Canvas, useFrame, useThree, useLoader } from '@react-three/fiber';
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { OrbitControls, Html } from '@react-three/drei';
import * as THREE from 'three';
import { STLExporter } from 'three/examples/jsm/exporters/STLExporter';
import { LampGeometry, generateBottomCapGeometry, LAMP_CONFIG } from 'lamp-core';
import './LampBuilder.css';
import ProfileEditor from './ProfileEditor';
import { defaultRenderingConfig } from '../config/renderingConfig';
import MenuTooltip from './MenuTooltip';
// import VideoUploader from './VideoUploader';
import { serializeLampDesign, deserializeLampDesign, testSerializationRoundTrip } from '../utils/lampSerializer';
import { useCart } from '../context/CartContext';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { createFootprintGeometry, createFootprintMesh } from 'lamp-core';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils';
import { addFeedback, generateLearnedDesign, getExtremeCombinations } from '../utils/designLearning';
import { saveLampDesign, getLampDesigns, getLampDesign, deleteLampDesign } from '../api/lampDesign';
import { saveDesignWithRating, updateDesignRating } from '../utils/ratingUtils';
import { pickWireframeGray, MIN_CONTRAST_RATIO, generateAestheticCompliantColorPair } from '../utils/colorContrastUtils';
import displacementGif from '../assets/videos/displacement.gif';
import twistGif from '../assets/videos/twist.gif';
import radialWaveGif from '../assets/videos/radial_wave.gif';
import radialWrinkleGif from '../assets/videos/radial_wrinkle.gif';
import heightmapNoiseGif from '../assets/videos/heightmap_noise.gif';
import facetGif from '../assets/videos/facet.gif';
import layerGif from '../assets/videos/layer.gif';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DesignCard from './DesignCard';
import MODIFIER_RETRY_CONFIG from '../modifierRetryConfig.js';
import { generateCachedLampThumbnail } from '../utils/thumbnailGenerator.js';
import { validateLampGeometry, setValidationDebug } from '../utils/geometryUtils';
import { generateRandomLamp } from '../utils/randomLampGenerator';
import { estimateSurfaceAreaFromDesign } from 'lamp-core';

// Helper function to ensure all modifier types are represented in the UI
function createCompleteModifierList(appliedModifiers) {
  const allModifierTypes = Object.keys(MODIFIER_CONFIGS);
  const appliedModifierMap = new Map(appliedModifiers.map(m => [m.type, m]));
  
  return allModifierTypes.map(type => {
    if (appliedModifierMap.has(type)) {
      // Use the applied modifier
      return appliedModifierMap.get(type);
    } else {
      // Create a disabled modifier with default parameters
      const config = MODIFIER_CONFIGS[type];
      return {
        type,
        disabled: true,
        is_symmetrical: config.is_symmetrical,
        parameters: Object.fromEntries(
          Object.entries(config.parameters).map(([key, value]) => [key, value.default])
        )
      };
    }
  });
}

// API configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Core constants
const SCALE_FACTOR = 0.01; // Convert mm to Three.js units (1 unit = 100mm)
const MAX_DIMENSION = 250; // mm
const TARGET_FACE_SIZE = 3; // Target face size in mm

// LocalStorage key for persisting lamp design
const LAMP_DESIGN_STORAGE_KEY = 'lamp_design_state';
// LocalStorage key for guest cacheId
const LAMP_DESIGN_CACHE_ID_KEY = 'lamp_design_cache_id';

// Utility function to get or create cacheId for guest users
function getOrCreateCacheId() {
  let cacheId = localStorage.getItem(LAMP_DESIGN_CACHE_ID_KEY);
  if (!cacheId) {
    // Generate a UUID-like string for cacheId
    cacheId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
    localStorage.setItem(LAMP_DESIGN_CACHE_ID_KEY, cacheId);
  }
  return cacheId;
}

// Rendering configuration constants
const RENDERING_CONFIG = {
  MATERIAL: {
    DEFAULT_METALNESS: 0,
    DEFAULT_ROUGHNESS: 0.5,
    DEFAULT_OPACITY: 0.95,
    DEFAULT_EMISSIVE_INTENSITY: 0
  },
  OVERHANG: {
    DEFAULT_COLOR: '#FF6B6B',
    DEFAULT_OPACITY: 0.5
  },
  CLASH: {
    DEFAULT_COLOR: '#4ECDC4',
    DEFAULT_OPACITY: 0.5
  },
  PRINTABLE_AREA: {
    DEFAULT_COLOR: '#FFE66D',
    DEFAULT_OPACITY: 0.5
  },
  VIEW: {
    DEFAULT_WIREFRAME_COLOR: 100,
    DEFAULT_WIREFRAME_THICKNESS: 1.0
  }
};

// Modifier types
const MODIFIER_TYPES = {
  DISPLACEMENT: 'displacement',
  TWIST: 'twist',
  RADIAL_WAVE: 'radial_wave',
  RADIAL_WRINKLE: 'radial_wrinkle',
  FACET: 'facet',
  LAYER: 'layer',
};

// Modifier configurations
const MODIFIER_CONFIGS = {
  [MODIFIER_TYPES.DISPLACEMENT]: {
    name: 'Displacement',
    is_symmetrical: false,
    parameters: {
      strength: { type: 'range', min: 1, max: 50, step: 1, default: 15 },
      noiseScale: { type: 'range', min: 0.001, max: 0.05, step: 0.001, default: 0.02, label: 'Scale' },
      translation: { type: 'range', min: 1, max: 1000, step: 10, default: 1, label: 'Pattern' }
    }
  },
  [MODIFIER_TYPES.RADIAL_WAVE]: {
    name: 'Radial Wave',
    is_symmetrical: false,
    parameters: {
      pattern: { 
        type: 'select', 
        options: [
          { value: 'sine', label: 'Gentle Wave' },
          { value: 'sine_squared', label: 'Soft Pulse' },
          { value: 'triangle', label: 'Sharp Edge' },
          { value: 'square', label: 'Block Pattern' },
          { value: 'sawtooth', label: 'Stepped Wave' }
        ],
        default: 'sine'
      },
      strength: { type: 'range', min: 1, max: 40, step: 1, default: 10 },
      wavesPerRevolution: { type: 'range', min: 1, max: 20, step: 1, default: 4 }
    }
  },
  [MODIFIER_TYPES.RADIAL_WRINKLE]: {
    name: 'Radial Wrinkle',
    is_symmetrical: false,
    parameters: {
      strength: { type: 'range', min: 0.001, max: 0.015, step: 0.001, default: 0.008 },
      perimeter: { type: 'range', min: 1, max: 100, step: 1, default: 100 }
    }
  },
  [MODIFIER_TYPES.FACET]: {
    name: 'Facet',
    is_symmetrical: true,
    parameters: {
      facetCount: { type: 'range', min: 3, max: 32, step: 1, default: 8, label: 'Number of Facets' },
      facetDepth: { type: 'range', min: 0.01, max: 1, step: 0.01, default: 0.5, label: 'Facet Depth' },
      facetBlend: { type: 'range', min: 0.1, max: 5, step: 0.1, default: 1, label: 'Blend Smoothness' }
    }
  },
  [MODIFIER_TYPES.LAYER]: {
    name: 'Layer',
    is_symmetrical: true,
    parameters: {
      layer_count: { 
        type: 'range', 
        min: 2, 
        max: 15, 
        step: 1, 
        default: 8, 
        label: 'Number of Layers' 
      },
      amplitude: { 
        type: 'range', 
        min: 1, 
        max: 50, 
        step: 1, 
        default: 8,
        label: 'Amplitude'
      },
      bunchiness: {
        type: 'range',
        min: 0.01,
        max: 0.1,
        step: 0.005,
        default: 0,
        label: 'Organic Variation'
      }
    }
  },
  [MODIFIER_TYPES.TWIST]: {
    name: 'Twist',
    is_symmetrical: false,
    parameters: {
      angle: { type: 'range', min: -500, max: 500, step: 1, default: 60 }
    }
  },
};

// Default footprint points
const DEFAULT_FOOTPRINT = [
  { x: -50, y: -50 },
  { x: 50, y: -50 },
  { x: 50, y: 50 },
  { x: -50, y: 50 }
];

// Initial state
const INITIAL_STATE = {
  diameter: 150,
  height: 200,
  profilePoints: [
    { x: 1.0, y: 0.0 },
    { x: 1.0, y: 0.9 }
  ],
  modifiers: Object.entries(MODIFIER_CONFIGS).map(([type, config]) => ({
    type,
    disabled: type !== MODIFIER_TYPES.DISPLACEMENT,
    is_symmetrical: config.is_symmetrical,
    parameters: Object.fromEntries(
      Object.entries(config.parameters).map(([key, value]) => [key, value.default])
    )
  })),
  renderingConfig: {
    ...defaultRenderingConfig,
    view: {
      ...defaultRenderingConfig.view,
      showGroundPlane: false,
      wireframeThickness: RENDERING_CONFIG.VIEW.DEFAULT_WIREFRAME_THICKNESS
    },
    material: {
      ...defaultRenderingConfig.material,
      metalness: RENDERING_CONFIG.MATERIAL.DEFAULT_METALNESS,
      roughness: RENDERING_CONFIG.MATERIAL.DEFAULT_ROUGHNESS,
      opacity: RENDERING_CONFIG.MATERIAL.DEFAULT_OPACITY,
      emissiveIntensity: RENDERING_CONFIG.MATERIAL.DEFAULT_EMISSIVE_INTENSITY
    },
    overhang: {
      ...defaultRenderingConfig.overhang,
      color: RENDERING_CONFIG.OVERHANG.DEFAULT_COLOR,
      opacity: RENDERING_CONFIG.OVERHANG.DEFAULT_OPACITY
    },
    clash: {
      ...defaultRenderingConfig.clash,
      color: RENDERING_CONFIG.CLASH.DEFAULT_COLOR,
      opacity: RENDERING_CONFIG.CLASH.DEFAULT_OPACITY
    },
    printableArea: {
      ...defaultRenderingConfig.printableArea,
      color: RENDERING_CONFIG.PRINTABLE_AREA.DEFAULT_COLOR,
      opacity: RENDERING_CONFIG.PRINTABLE_AREA.DEFAULT_OPACITY
    }
  }
};

// Modifier component
function Modifier({ modifier, index, onUpdate, onRemove }) {
  const config = MODIFIER_CONFIGS[modifier.type];
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipTrigger, setTooltipTrigger] = useState(null);

  const handleTooltipShow = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    
    // Calculate initial position
    let x = rect.left - 290; // Position to the left of the button
    let y = rect.top;

    // Get viewport dimensions
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    // More accurate tooltip dimensions - these should match the CSS
    const tooltipWidth = 240; // Width from CSS
    const tooltipPadding = 24; // 12px top + 12px bottom padding
    const tooltipHeaderHeight = 30; // Approximate header height
    const tooltipDescriptionHeight = 60; // Approximate description height
    const tooltipContentHeight = 230; // Height for video/gif content
    const tooltipHeight = tooltipPadding + tooltipHeaderHeight + tooltipDescriptionHeight + tooltipContentHeight;

    // Check if tooltip would go below viewport
    if (y + tooltipHeight > viewportHeight - 20) {
      // Position tooltip above the trigger point
      y = rect.bottom - tooltipHeight - 20;
    }

    // Ensure tooltip doesn't go above viewport
    y = Math.max(20, y);
    
    // Check if tooltip would go off-screen to the left
    if (x < 20) {
      // Position tooltip to the right of the button instead
      x = rect.right + 20;
    }
    
    // Ensure tooltip doesn't go off-screen to the right
    if (x + tooltipWidth > viewportWidth - 20) {
      x = viewportWidth - tooltipWidth - 20;
    }
    
    // Final safety check - ensure tooltip is within viewport
    if (y + tooltipHeight > viewportHeight) {
      y = viewportHeight - tooltipHeight - 20;
    }
    
    setTooltipPosition({ x, y });
    setTooltipTrigger(event.currentTarget);
    setShowTooltip(true);
  };

  const handleTooltipHide = () => {
    setShowTooltip(false);
    setTooltipTrigger(null);
  };

  const handleParameterChange = (paramName, value) => {
    // Add snap-to-zero functionality for twist angle
    if (modifier.type === MODIFIER_TYPES.TWIST && paramName === 'angle') {
      // Snap to zero if within 5 degrees
      if (Math.abs(value) < 5) {
        value = 0;
      }
    }
    
    onUpdate(index, {
      ...modifier,
      parameters: {
        ...modifier.parameters,
        [paramName]: value
      }
    });
  };

  const handleToggleDisable = () => {
    onUpdate(index, {
      ...modifier,
      disabled: !modifier.disabled
    });
  };

  // Helper function to format parameter value
  const formatParameterValue = (paramName, value, paramConfig) => {
    if (paramConfig.type === 'select') {
      return paramConfig.options.find(opt => opt.value === value)?.label;
    }
    
    // Calculate decimal places based on step value
    const step = paramConfig.step;
    const decimalPlaces = step < 1 ? Math.abs(Math.log10(step)) : 0;
    
    // Format the value with appropriate decimal places
    const formattedValue = value.toFixed(decimalPlaces);
    
    // Add unit if needed
    return `${formattedValue}${modifier.type === MODIFIER_TYPES.TWIST && paramName === 'angle' ? '°' : ''}`;
  };

  return (
    <div className={`modifier ${modifier.disabled ? 'disabled' : ''}`}>
      <div className="modifier-header">
        <span>{config.name}</span>
        <div className="modifier-actions">
          <button 
            className="tooltip-trigger"
            onClick={(e) => {
              e.stopPropagation();
              handleTooltipShow(e);
            }}
            onMouseLeave={handleTooltipHide}
          >
            ?
          </button>
          <button 
            onClick={handleToggleDisable}
            title={modifier.disabled ? "Enable modifier" : "Disable modifier"}
            className="toggle-button"
          >
            {modifier.disabled ? '⚪' : '⚫'}
          </button>
          <button 
            onClick={() => onRemove(index)}
            title="Remove modifier"
          >
            ×
          </button>
        </div>
      </div>
      {!modifier.disabled && (
        <div className="modifier-parameters">
          {Object.entries(config.parameters).map(([paramName, paramConfig]) => (
            <div key={paramName} className="control-group">
              <label>
                {paramConfig.label || paramName.charAt(0).toUpperCase() + paramName.slice(1)}: 
                <span style={{ 
                  marginLeft: '8px', 
                  color: modifier.type === MODIFIER_TYPES.TWIST && paramName === 'angle' && modifier.parameters[paramName] === 0 ? '#00aa00' : '#666'
                }}>
                  {formatParameterValue(paramName, modifier.parameters[paramName], paramConfig)}
                </span>
              </label>
              {paramConfig.type === 'select' ? (
                <select
                  value={modifier.parameters[paramName]}
                  onChange={(e) => handleParameterChange(paramName, e.target.value)}
                >
                  {paramConfig.options.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="range"
                  min={paramConfig.min}
                  max={paramConfig.max}
                  step={paramConfig.step}
                  value={modifier.parameters[paramName]}
                  onChange={(e) => handleParameterChange(paramName, parseFloat(e.target.value))}
                />
              )}
            </div>
          ))}
        </div>
      )}
      {showTooltip && (
        <MenuTooltip
          {...MENU_TOOLTIPS[modifier.type]}
          isVisible={true}
          position={tooltipPosition}
          triggerElement={tooltipTrigger}
        />
      )}
    </div>
  );
}

// ModifierList component
function ModifierList({ modifiers, onUpdate, onRemove }) {
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipTrigger, setTooltipTrigger] = useState(null);

  const handleTooltipShow = (modifierType, event) => {
    // Find the closest modifier-header element
    const headerElement = event.currentTarget.closest('.modifier-header');
    const headingSpan = headerElement.querySelector('span');
    const rect = headingSpan.getBoundingClientRect();
    
    // Calculate initial position
    let x = rect.left + window.scrollX - 290;
    let y = rect.top + window.scrollY - 10;

    // Get viewport dimensions
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    // Conservative tooltip dimensions - account for scrollable content
    const tooltipWidth = 240; // Width from CSS
    const tooltipMaxHeight = viewportHeight - 40; // From CSS: calc(100vh - 20px) + margin
    const tooltipMinHeight = 350; // Minimum height for content (header + description + video/gif)
    
    // Use the smaller of max height or minimum content height
    const tooltipHeight = Math.min(tooltipMaxHeight, tooltipMinHeight);

    // Check if tooltip would go below viewport
    if (y + tooltipHeight > viewportHeight + window.scrollY - 30) {
      // Position tooltip above the trigger point
      y = rect.bottom + window.scrollY - tooltipHeight - 30;
    }

    // Ensure tooltip doesn't go above viewport
    y = Math.max(window.scrollY + 30, y);
    
    // Check if tooltip would go off-screen to the left
    if (x < 30) {
      // Position tooltip to the right of the button instead
      x = rect.right + window.scrollX + 30;
    }
    
    // Ensure tooltip doesn't go off-screen to the right
    if (x + tooltipWidth > viewportWidth - 30) {
      x = viewportWidth - tooltipWidth - 30;
    }
    
    // Final safety check - ensure tooltip is within viewport
    if (y + tooltipHeight > viewportHeight + window.scrollY) {
      y = viewportHeight + window.scrollY - tooltipHeight - 30;
    }
    
    setTooltipPosition({ x, y });
    setTooltipTrigger(event.currentTarget);
    setActiveTooltip(modifierType);
  };

  const handleTooltipHide = () => {
    setActiveTooltip(null);
    setTooltipTrigger(null);
  };

  // Helper function to format parameter value
  const formatParameterValue = (paramName, value, paramConfig, modifierType) => {
    if (paramConfig.type === 'select') {
      return paramConfig.options.find(opt => opt.value === value)?.label;
    }
    
    // Calculate decimal places based on step value
    const step = paramConfig.step;
    const decimalPlaces = step < 1 ? Math.abs(Math.log10(step)) : 0;
    
    // Format the value with appropriate decimal places
    const formattedValue = value.toFixed(decimalPlaces);
    
    // Add unit if needed
    return `${formattedValue}${modifierType === MODIFIER_TYPES.TWIST && paramName === 'angle' ? '°' : ''}`;
  };

  return (
    <div className="modifier-list">

      {modifiers.map((modifier, index) => (
        <div
          key={index}
          className="modifier"
        >
          <div className="modifier-header">
            <span>{MODIFIER_CONFIGS[modifier.type].name}</span>
            <div className="modifier-actions">
              <button 
                className="tooltip-trigger"
                onClick={(e) => {
                  e.stopPropagation();
                  handleTooltipShow(modifier.type, e);
                }}
                onMouseLeave={handleTooltipHide}
              >
                ?
              </button>
              <div className="toggle-container">
                <input
                  type="checkbox"
                  checked={!modifier.disabled}
                  onChange={() => onUpdate(index, { ...modifier, disabled: !modifier.disabled })}
                  title={modifier.disabled ? "Enable modifier" : "Disable modifier"}
                />
              </div>
            </div>
          </div>
          {!modifier.disabled && (
            <div className="modifier-parameters">
              {Object.entries(MODIFIER_CONFIGS[modifier.type].parameters).map(([paramName, paramConfig]) => (
                <div key={paramName} className="control-group">
                  <label>
                    {paramConfig.label || paramName.charAt(0).toUpperCase() + paramName.slice(1)}: 
                    <span style={{ 
                      marginLeft: '8px', 
                      color: modifier.type === MODIFIER_TYPES.TWIST && paramName === 'angle' && modifier.parameters[paramName] === 0 ? '#00aa00' : '#666'
                    }}>
                      {formatParameterValue(paramName, modifier.parameters[paramName], paramConfig, modifier.type)}
                    </span>
                  </label>
                  {paramConfig.type === 'select' ? (
                    <select
                      value={modifier.parameters[paramName]}
                      onChange={(e) => onUpdate(index, {
                        ...modifier,
                        parameters: {
                          ...modifier.parameters,
                          [paramName]: e.target.value
                        }
                      })}
                    >
                      {paramConfig.options.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="range"
                      min={paramConfig.min}
                      max={paramConfig.max}
                      step={paramConfig.step}
                      value={modifier.parameters[paramName]}
                      onChange={(e) => onUpdate(index, {
                        ...modifier,
                        parameters: {
                          ...modifier.parameters,
                          [paramName]: parseFloat(e.target.value)
                        }
                      })}
                    />
                  )}
                  {modifier.type === MODIFIER_TYPES.TWIST && paramName === 'angle' && (
                    <button
                      onClick={() => onUpdate(index, {
                        ...modifier,
                        parameters: {
                          ...modifier.parameters,
                          [paramName]: 0
                        }
                      })}
                      style={{
                        marginLeft: '8px',
                        padding: '2px 8px',
                        backgroundColor: '#00aa00',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      title="Reset to zero"
                    >
                      Reset
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      {activeTooltip && (
        <MenuTooltip
          {...MENU_TOOLTIPS[activeTooltip]}
          isVisible={true}
          position={tooltipPosition}
          triggerElement={tooltipTrigger}
        />
      )}
    </div>
  );
}

// Add this function before the Lamp component
function checkOverhang(normal, angleThreshold, position = { x: 0, y: 0, z: 0 }) {
  // Skip overhang check for bottom cap and its extrusion (y <= 0)
  const EPSILON = 0.001;
  if (position.y <= EPSILON) {
    return false;
  }

  // Normalize the normal vector
  const norm = Math.sqrt(normal.x * normal.x + normal.y * normal.y + normal.z * normal.z);
  const normalizedNormal = {
    x: normal.x / norm,
    y: normal.y / norm,
    z: normal.z / norm
  };

  // Calculate dot product with up vector (0,1,0)
  const dotProduct = Math.abs(normalizedNormal.y);

  // Calculate angle in radians
  const angle = Math.acos(dotProduct);

  // Calculate overhang angle (90 degrees - angle)
  const overhangAngle = (Math.PI / 2) - angle;

  // Convert threshold from degrees to radians
  const thresholdRadians = angleThreshold * Math.PI / 180;

  if (overhangAngle > thresholdRadians) {
    return true;
  }
  return false;
}

// Add this new component before the Lamp component
function PrintableAreaBounds({ visible }) {
  const boundsMaterial = useMemo(() => {
    return new THREE.MeshBasicMaterial({
      color: '#ff0000',
      transparent: true,
      opacity: 0.2,
      wireframe: true,
      side: THREE.DoubleSide
    });
  }, []);

  const boundsGeometry = useMemo(() => {
    return new THREE.BoxGeometry(250, 250, 250);
  }, []);

  if (!visible) return null;

  return (
    <mesh
      geometry={boundsGeometry}
      material={boundsMaterial}
      position={[0, 125, 0]} // Center the box vertically
    />
  );
}

function Lamp({ 
  diameter, 
  height, 
  tubularSegments, 
  radialSegments, 
  profilePoints, 
  modifiers, 
  noiseGen,
  showWireframe,
  showDebug,
  vertexDebug,
  onHoverVertex,
  hoveredVertex,
  angleThreshold = defaultRenderingConfig.overhang.threshold,
  onGeometriesUpdate,
  showVertices,
  renderingConfig = defaultRenderingConfig,
  onOverhangChange,
  showFootprint,
  showPrintableAreaBounds,
  sharedClock,
  hasPrintableAreaClash = false,
  shadowsEnabled
}) {
  // Create geometry using lamp-core
  const geometry = useMemo(() => {
    const lampGeometry = new LampGeometry(
      {
        diameter,
        height,
        tubularSegments,
        radialSegments,
        profilePoints
      },
      modifiers.map(modifier => ({
        ...modifier,
        parameters: { ...modifier.parameters }
      })),
      sharedClock // Use sharedClock as noise seed for deterministic generation
    );
    return lampGeometry.getGeometry();
  }, [
    diameter, 
    height, 
    tubularSegments, 
    radialSegments, 
    profilePoints, 
    JSON.stringify(modifiers.map(m => ({ type: m.type, disabled: m.disabled, parameters: m.parameters })))
  ]);

  // Load the base model with error handling
  const baseModel = useLoader(STLLoader, '/models/lamp-base/globe_E14 v1.stl', (loader) => {
    console.log('Loading base model...');
  }, (error) => {
    console.error('Error loading base model:', error);
  });

  // Create a material for the base model
  const baseMaterial = useMemo(() => {
    return new THREE.MeshStandardMaterial({
      color: '#ffffff', // White color
      roughness: 0.7,
      metalness: 0.3,
      side: THREE.DoubleSide
    });
  }, []);

  // Create a mesh for the base model
  const baseMesh = useMemo(() => {
    if (!baseModel) return null;
    const mesh = new THREE.Mesh(baseModel, baseMaterial);
    
    // Compute the bounding box to find the lowest point
    const boundingBox = new THREE.Box3().setFromObject(mesh);
    const minY = boundingBox.min.y;
    
    // Move the mesh up by the absolute value of minY to place it on the ground plane
    mesh.position.y = -minY;
    
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    return mesh;
  }, [baseModel, baseMaterial]);
  
  const outerRadius = diameter / 2;  // Use actual millimeter value
  const heightScaled = height;  // Use actual millimeter value
  const intervalRef = useRef(null);
  const geometryRef = useRef(null);
  const wireframeRef = useRef(null);

  // Create noise texture for subtle surface detail - moved before material creation
  const noiseTexture = useMemo(() => {
    const size = 256;
    const data = new Uint8Array(size * size * 4);
    
    for (let i = 0; i < size * size * 4; i += 4) {
      const x = (i / 4) % size;
      const y = Math.floor((i / 4) / size);
      
      // Generate more pronounced noise pattern
      const noise = noiseGen.perlin2(x / 8, y / 8) * 0.5 + 0.5;
      const value = Math.floor(noise * 255);
      
      data[i] = value;     // r
      data[i + 1] = value; // g
      data[i + 2] = value; // b
      data[i + 3] = 255;   // a
    }
    
    const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(2, 2);
    texture.needsUpdate = true;
    return texture;
  }, [noiseGen]); // Only depend on noiseGen, not on every render

  // Create material based on type
  const material = useMemo(() => {
    if (!renderingConfig) return null;
    const color = new THREE.Color(renderingConfig.material.color);
    return new THREE.MeshStandardMaterial({
      color: color,
      roughness: renderingConfig.material.roughness ?? 0.5,
      metalness: renderingConfig.material.metalness ?? 0.1,
      opacity: renderingConfig.material.opacity || 1,
      transparent: (renderingConfig.material.opacity || 1) < 1,
      side: THREE.DoubleSide
    });
  }, [renderingConfig.material.color, renderingConfig.material.opacity, renderingConfig.material.roughness, renderingConfig.material.metalness]);

  // Create bottom cap mesh - moved after material creation and as a separate step
  const bottomCapMesh = useMemo(() => {
    if (!geometry || !material || !renderingConfig) return null;
    // Generate bottom cap geometry using core function
    const extrusionHeight = 10; // Default value used in generateBottomCapGeometry
    const bottomCapGeometry = generateBottomCapGeometry(geometry, extrusionHeight);
    // Use the same MeshBasicMaterial as the lamp mesh
    const capColor = new THREE.Color(renderingConfig.material.color);
    const capMaterial = new THREE.MeshBasicMaterial({
      color: capColor,
      opacity: renderingConfig.material.opacity || 1,
      transparent: (renderingConfig.material.opacity || 1) < 1,
      side: THREE.DoubleSide
    });
    const mesh = new THREE.Mesh(bottomCapGeometry, capMaterial);
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.renderOrder = 0;

    // Extract bottomVertices from the main geometry at y=0, then create perimeter at y=-extrusionHeight
    const mainPosAttr = geometry.getAttribute('position');
    const bottomVertices = [];
    for (let i = 0; i < mainPosAttr.count; i++) {
      const y = mainPosAttr.getY(i);
      if (Math.abs(y) < 0.001) {
        bottomVertices.push(new THREE.Vector3(mainPosAttr.getX(i), -extrusionHeight, mainPosAttr.getZ(i)));
      }
    }
    if (bottomVertices.length > 2) {
      // Get the lamp material color and calculate compliant wireframe color
      const lampColor = new THREE.Color(renderingConfig.material.color);
      const compliantWireframeColor = pickWireframeGray(lampColor, MIN_CONTRAST_RATIO);
      
      const wireframeHex = new THREE.Color(compliantWireframeColor.r, compliantWireframeColor.g, compliantWireframeColor.b).getHex();
      const wireframeMaterial = new THREE.LineBasicMaterial({
        color: wireframeHex,
        transparent: true,
        opacity: 1.0,
        linewidth: 2 // Thicker line for visibility
      });
      // Perimeter outline
      const geometry = new THREE.BufferGeometry().setFromPoints(bottomVertices);
      const lineLoop = new THREE.LineLoop(geometry, wireframeMaterial);
      mesh.add(lineLoop);
      // Vertical lines (spokes)
      const spokes = [];
      for (let i = 0; i < bottomVertices.length; i++) {
        const v0 = new THREE.Vector3(bottomVertices[i].x, 0, bottomVertices[i].z);
        const v1 = new THREE.Vector3(bottomVertices[i].x, -extrusionHeight, bottomVertices[i].z);
        spokes.push(v0, v1);
      }
      if (spokes.length > 0) {
        const spokesGeometry = new THREE.BufferGeometry().setFromPoints(spokes);
        const spokesLines = new THREE.LineSegments(spokesGeometry, wireframeMaterial);
        mesh.add(spokesLines);
      }
    }

    bottomCapGeometry.dispose();
    return mesh;
  }, [geometry, renderingConfig.material.color, renderingConfig.material.opacity]);

  // Create main mesh with material
  const mainMesh = useMemo(() => {
    if (!material) return null;
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = renderingConfig.shadows.enabled;
    mesh.receiveShadow = renderingConfig.shadows.enabled;
    mesh.renderOrder = 0;
    return mesh;
  }, [geometry, material, renderingConfig.shadows.enabled]);

  // Add debug logging to help diagnose issues
  useEffect(() => {
    if (geometry) {
      const positions = geometry.attributes.position.array;
      let bottomVertexCount = 0;
      for (let i = 0; i < positions.length; i += 3) {
        if (Math.abs(positions[i + 1]) <= 0.001) {
          bottomVertexCount++;
        }
      }
    }
  }, [geometry]);

  // Add effect to call onGeometriesUpdate when geometry changes
  useEffect(() => {
    if (geometry && onGeometriesUpdate) {
      onGeometriesUpdate({ geometry });
    }
  }, [geometry, onGeometriesUpdate]);

  // Create overhang monitoring shader material
  const angleMaterial = useMemo(() => {
    const material = new THREE.ShaderMaterial({
      uniforms: {
        angleThreshold: { value: angleThreshold * Math.PI / 180 },
        overhangOpacity: { value: renderingConfig.overhang.opacity },
        overhangColor: { value: new THREE.Color(renderingConfig.overhang.color || '#ff0000') },
        time: { value: 0 }
      },
      vertexShader: `
        varying vec3 vNormal;
        varying vec3 vPosition;
        void main() {
          vNormal = normalize(normal);
          vPosition = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float angleThreshold;
        uniform float overhangOpacity;
        uniform vec3 overhangColor;
        uniform float time;
        varying vec3 vNormal;
        varying vec3 vPosition;
        
        void main() {
          // Skip overhang check for bottom cap and its extrusion (y <= 0)
          const float EPSILON = 0.001;
          if (vPosition.y <= EPSILON) {
            discard;
          }

          float angle = acos(abs(dot(normalize(vNormal), vec3(0.0, 1.0, 0.0))));
          float overhangAngle = (3.14159 / 2.0) - angle;
          
          if (overhangAngle > angleThreshold) {
            float pulse = (sin(time * 3.14159) * 0.5 + 0.5);
            float finalOpacity = mix(0.2 * overhangOpacity, overhangOpacity, pulse);
            gl_FragColor = vec4(overhangColor, finalOpacity);
          } else {
            discard;
          }
        }
      `,
      transparent: true,
      side: THREE.DoubleSide,
      depthTest: true,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -4
    });

    return material;
  }, [angleThreshold, renderingConfig.overhang.opacity, renderingConfig.overhang.color]);

  // Create clash visualization shader material
  const clashMaterial = useMemo(() => {
    const material = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        clashOpacity: { value: renderingConfig.clash.opacity || 0.5 },
        clashColor: { value: new THREE.Color(renderingConfig.clash.color || '#0088ff') }
      },
      vertexShader: `
        varying vec3 vPosition;
        void main() {
          vPosition = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float time;
        uniform float clashOpacity;
        uniform vec3 clashColor;
        varying vec3 vPosition;
        
        void main() {
          float pulse = (sin(time * 3.14159) * 0.5 + 0.5);
          float opacity = mix(0.2 * clashOpacity, clashOpacity, pulse);
          
          float dist = sqrt(vPosition.x * vPosition.x + vPosition.z * vPosition.z);
          
          // Skip clash detection for bottom cap and its extrusion (y <= 0)
          const float EPSILON = 0.001;
          if (vPosition.y <= EPSILON) {
            discard;
          }
          
          // Check for clashes in both the light base and light globe areas
          if ((dist <= 45.0 && vPosition.y <= 10.0) || // Light base clash (90mm diameter, 10mm height)
              (dist <= 15.0 && vPosition.y <= 200.0)) { // Light globe clash (30mm diameter, 200mm height)
            gl_FragColor = vec4(clashColor, opacity);
          } else {
            discard;
          }
        }
      `,
      transparent: true,
      side: THREE.DoubleSide,
      depthTest: true,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -4
    });

    return material;
  }, [renderingConfig.clash.color, renderingConfig.clash.opacity]);

  // Create printable area clash visualization shader material
  const printableAreaClashMaterial = useMemo(() => {
    const material = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        clashOpacity: { value: renderingConfig.printableArea.opacity || 0.5 },
        clashColor: { value: new THREE.Color(renderingConfig.printableArea.color || '#ffd700') }
      },
      vertexShader: `
        varying vec3 vPosition;
        void main() {
          vPosition = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float time;
        uniform float clashOpacity;
        uniform vec3 clashColor;
        varying vec3 vPosition;
        
        void main() {
          float pulse = (sin(time * 3.14159) * 0.5 + 0.5);
          float opacity = mix(0.2 * clashOpacity, clashOpacity, pulse);
          
          if (abs(vPosition.x) > 125.0 || vPosition.y > 250.0 || abs(vPosition.z) > 125.0) {
            gl_FragColor = vec4(clashColor, opacity);
          } else {
            discard;
          }
        }
      `,
      transparent: true,
      side: THREE.DoubleSide,
      depthTest: true,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -4
    });

    return material;
  }, [renderingConfig.printableArea.opacity, renderingConfig.printableArea.color]);

  // Update uniforms with animation frame
  useFrame((state) => {
    if (angleMaterial && showDebug) {
      // Update time uniform (1Hz = one cycle every second)
      angleMaterial.uniforms.time.value = state.clock.elapsedTime;
    }
    if (clashMaterial && !showFootprint) {
      clashMaterial.uniforms.time.value = state.clock.elapsedTime;
    }
    if (printableAreaClashMaterial) {
      printableAreaClashMaterial.uniforms.time.value = state.clock.elapsedTime;
    }
  });

  // Update angle threshold uniform when it changes
  useEffect(() => {
    if (angleMaterial) {
      angleMaterial.uniforms.angleThreshold.value = angleThreshold * Math.PI / 180;
    }
  }, [angleThreshold, angleMaterial]);

  // Update opacity uniform when it changes
  useEffect(() => {
    if (angleMaterial) {
      angleMaterial.uniforms.overhangOpacity.value = renderingConfig.overhang.opacity;
    }
  }, [renderingConfig.overhang.opacity, angleMaterial]);

  // Create wireframe material
  const wireframeMaterial = useMemo(() => {
    // Get the lamp material color
    const lampColor = new THREE.Color(renderingConfig.material.color);
    // Calculate compliant wireframe color
    const compliantWireframeColor = pickWireframeGray(lampColor, MIN_CONTRAST_RATIO);
    // Convert {r,g,b} in [0,1] to hex for Three.js
    const wireframeHex = new THREE.Color(compliantWireframeColor.r, compliantWireframeColor.g, compliantWireframeColor.b).getHex();
    return new THREE.MeshBasicMaterial({
      color: wireframeHex,
      wireframe: true,
      transparent: true,
      opacity: 0.5
      // linewidth removed: not supported by MeshBasicMaterial
    });
  }, [renderingConfig.material.color, renderingConfig.view.wireframeThickness]);

  // Create edges geometry - reuse the main geometry
  const edges = useMemo(() => {
    try {
      const edgesGeometry = new THREE.EdgesGeometry(geometry, Math.PI / 2);
      return edgesGeometry;
    } catch (error) {
      console.warn('Failed to create edges geometry:', error);
      return new THREE.BufferGeometry();
    }
  }, [geometry]);

  // Create debug mesh for overhang visualization
  const debugMesh = useMemo(() => {
    if (!showDebug) return null;
    const mesh = new THREE.Mesh(geometry, angleMaterial);
    mesh.renderOrder = 1; // Ensure it renders after the main mesh
    return mesh;
  }, [geometry, angleMaterial, showDebug]);

  // Create wireframe mesh - reuse geometry
  const wireframeMesh = useMemo(() => {
    const mesh = new THREE.Mesh(geometry, wireframeMaterial);
    wireframeRef.current = mesh;
    return mesh;
  }, [geometry, wireframeMaterial]);

  // Update wireframe position to follow camera
  useFrame(({ camera }) => {
    if (wireframeRef.current && showWireframe) {
      // Get camera direction
      const cameraDirection = new THREE.Vector3();
      camera.getWorldDirection(cameraDirection);
      
      // Move wireframe slightly towards camera
      wireframeRef.current.position.copy(cameraDirection.multiplyScalar(-0.5));
    }
  });

  // Create vertex points - only when needed
  const vertexGeometry = useMemo(() => {
    if (!showVertices && !vertexDebug) return null;
    if (!geometry || !geometry.attributes || !geometry.attributes.position) return null;
    
    const vertexGeometry = new THREE.BufferGeometry();
    const vertexPositions = [];
    const colors = [];

    // Get positions from the main geometry
    const mainPositions = geometry.attributes.position.array;

    // Add vertices
    for (let i = 0; i < mainPositions.length; i += 3) {
      vertexPositions.push(
        mainPositions[i],
        mainPositions[i + 1],
        mainPositions[i + 2]
      );
      
      // Check if this is a seam vertex
      const positionInRing = (i / 3) % (radialSegments + 1);
      const isSeamVertex = positionInRing === 0 || positionInRing === radialSegments;
      
      // Set color based on whether it's a seam vertex
      if (isSeamVertex && showVertices) {
        colors.push(1, 0, 0); // Red for seam vertices
      } else {
        colors.push(0, 0, 0); // Black for regular vertices
      }
    }

    vertexGeometry.setAttribute('position', new THREE.Float32BufferAttribute(vertexPositions, 3));
    vertexGeometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

    return { geometry: vertexGeometry, positions: vertexPositions, colors };
  }, [showVertices, vertexDebug, geometry, radialSegments]);

  // Handle vertex hover
  const handleVertexHover = (event) => {
    if (!vertexDebug) return;

    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    
    // Calculate mouse position in normalized device coordinates
    const rect = event.target.getBoundingClientRect();
    mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    raycaster.setFromCamera(mouse, event.target.camera);

    const intersects = raycaster.intersectObject(event.target.scene.children[0]);
    if (intersects.length > 0) {
      const vertexIndex = intersects[0].face.a;
      const position = geometry.attributes.position;
      const vertex = {
        index: vertexIndex,
        position: {
          x: position.getX(vertexIndex),
          y: position.getY(vertexIndex),
          z: position.getZ(vertexIndex)
        },
        ring: Math.floor(vertexIndex / (radialSegments + 1)),
        segment: vertexIndex % (radialSegments + 1)
      };
      onHoverVertex(vertex);
    } else {
      onHoverVertex(null);
    }
  };

  // Add cleanup effect
  useEffect(() => {
    return () => {
      // Cleanup geometries when component unmounts
      if (geometry) geometry.dispose();
      if (edges) edges.dispose();
      if (vertexGeometry?.geometry) {
        vertexGeometry.geometry.dispose();
      }
    };
  }, [geometry, edges, vertexGeometry]);

  useEffect(() => {
    if (!geometry) {
      return;
    }

    geometryRef.current = geometry;

    // Immediate check when geometry changes
    const checkForOverhang = () => {
      if (!geometryRef.current) return;
      
      const positions = geometryRef.current.attributes.position.array;
      const normals = geometryRef.current.attributes.normal.array;
      
      let foundOverhang = false;

      for (let i = 0; i < positions.length; i += 3) {
        const normal = {
          x: normals[i],
          y: normals[i + 1],
          z: normals[i + 2]
        };
        
        const position = {
          x: positions[i],
          y: positions[i + 1],
          z: positions[i + 2]
        };
        
        if (checkOverhang(normal, angleThreshold, position)) {
          foundOverhang = true;
          break;
        }
      }

      onOverhangChange(foundOverhang);
    };

    // Run immediate check
    checkForOverhang();

    // Set up interval for continuous checking
    if (!intervalRef.current) {
      intervalRef.current = setInterval(checkForOverhang, 500);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [geometry, angleThreshold, onOverhangChange]);

  // Create ground plane
  const groundPlane = useMemo(() => {
    if (!renderingConfig.view.showGroundPlane) return null;
    const plane = new THREE.Mesh(
      new THREE.PlaneGeometry(1000, 1000),
      new THREE.MeshStandardMaterial({ color: 0x888888, roughness: 1 })
    );
    plane.rotation.x = -Math.PI / 2;
    plane.position.y = -0.01;
    plane.receiveShadow = renderingConfig.shadows.enabled;
    plane.visible = renderingConfig.view.showGroundPlane;
    return plane;
  }, [renderingConfig.shadows.enabled, renderingConfig.view.showGroundPlane]);

  // Create clash visualization mesh
  const clashMesh = useMemo(() => {
    if (showFootprint) return null;
    const mesh = new THREE.Mesh(geometry, clashMaterial);
    mesh.renderOrder = 1;
    return mesh;
  }, [geometry, clashMaterial, showFootprint]);

  // Create printable area clash visualization mesh
  const printableAreaClashMesh = useMemo(() => {
    if (!hasPrintableAreaClash) return null;
    const mesh = new THREE.Mesh(geometry, printableAreaClashMaterial);
    mesh.renderOrder = 1;
    return mesh;
  }, [geometry, printableAreaClashMaterial, hasPrintableAreaClash]);

  const mainMeshRef = useRef();
  const groundPlaneRef = useRef();
  useEffect(() => {
    if (mainMeshRef.current) {
      mainMeshRef.current.castShadow = shadowsEnabled;
      mainMeshRef.current.receiveShadow = shadowsEnabled;
    }
    if (groundPlaneRef.current) {
      groundPlaneRef.current.receiveShadow = shadowsEnabled;
    }
  }, [shadowsEnabled]);

  // 2. In Lamp, log when refs are set and their shadow values
  useEffect(() => {
    if (mainMeshRef.current) {
      console.log('[Lamp] mainMeshRef set:', mainMeshRef.current);
      console.log('[Lamp] mainMesh castShadow:', mainMeshRef.current.castShadow, 'receiveShadow:', mainMeshRef.current.receiveShadow);
      if (mainMeshRef.current.material) {
        console.log('[Lamp] mainMesh material type:', mainMeshRef.current.material.type);
      }
    }
    if (groundPlaneRef.current) {
      console.log('[Lamp] groundPlaneRef set:', groundPlaneRef.current);
      console.log('[Lamp] groundPlane receiveShadow:', groundPlaneRef.current.receiveShadow);
    }
  }, [shadowsEnabled, mainMeshRef, groundPlaneRef]);

  return (
    <group>
      {/* Ground plane */}
      {renderingConfig.view.showGroundPlane && (
        <primitive ref={groundPlaneRef} object={groundPlane} />
      )}

      {/* Printable area bounds */}
      <PrintableAreaBounds visible={showPrintableAreaBounds} />

      {/* Base model */}
      {baseMesh && (
        <primitive 
          object={baseMesh} 
          position={[0, 0, 0]}
          scale={[1, 1, 1]}
          rotation={[0, 0, 0]}
        />
      )}
      
      {/* Main shaded mesh - always visible */}
      <primitive ref={mainMeshRef} object={mainMesh} position={[0, 0, 0]} />

      {/* Bottom cap mesh - separate from main mesh */}
      {bottomCapMesh && (
        <primitive 
          object={bottomCapMesh} 
          position={[0, 0, 0]}
        />
      )}
      
      {/* Overhang debug mesh */}
      {showDebug && debugMesh && (
        <primitive 
          object={debugMesh}
          position={[0, 0, 0]}
        />
      )}
      
      {/* Wireframe overlay - only visible when showWireframe is true */}
      {showWireframe && (
        <primitive 
          object={wireframeMesh} 
          position={[0, 0, 0]}
        />
      )}

      {/* Footprint visualization - only when showFootprint is true */}
      {showFootprint && (
        <primitive 
          object={createFootprintMesh(createFootprintGeometry(LAMP_CONFIG.FOOTPRINT_RADIUS, height))}
          position={[0, 0, 0]}
          rotation={[0, 0, 0]}
        />
      )}

      {/* Clash visualization on mesh - only when showFootprint is false */}
      {!showFootprint && clashMesh && (
        <primitive 
          object={clashMesh}
          position={[0, 0, 0]}
        />
      )}

      {/* Printable area clash visualization - always visible when there's a clash */}
      {hasPrintableAreaClash && printableAreaClashMesh && (
        <primitive 
          object={printableAreaClashMesh}
          position={[0, 0, 0]}
        />
      )}
    </group>
  );
}

const MENU_TOOLTIPS = {
  profile: {
    title: "Profile Editor",
    description: "Define the basic shape of your lamp. Add points by clicking, adjust the shape by dragging points, and remove points with right-click.",
  },
  modifiers: {
    title: "Modifiers",
    description: "Add effects to transform your lamp's shape. Each modifier can be enabled/disabled and adjusted with its own parameters. Combine multiple modifiers for complex effects.",
  },
  displacement: {
    title: "Shape Variation",
    description: "Modify the lamp's surface shape. The strength control determines how much the surface moves, while the scale control adjusts the size of the variations.",
    videoUrl: displacementGif
  },
  twist: {
    title: "Twist Effect",
    description: "Rotate the lamp's shape around its vertical axis. The angle control determines the amount of rotation, with higher values creating more pronounced twists.",
    videoUrl: twistGif
  },
  radial_wave: {
    title: "Wave Pattern",
    description: "Create circular variations in the lamp's shape. Select from different wave types and adjust the pattern's strength and frequency.",
    videoUrl: radialWaveGif
  },
  radial_wrinkle: {
    title: "Wrinkle Effect",
    description: "Add radial shape variations. Control the number of wrinkles and their intensity using the perimeter and variation parameters.",
    videoUrl: radialWrinkleGif
  },
  heightmap_noise: {
    title: "Heightmap Noise",
    description: "Creates layered, organic surface effects using quantized noise. Adjust amplitude, frequency, and layer count for different looks.",
    videoUrl: heightmapNoiseGif
  },
  facet: {
    title: "Facet Effect",
    description: "Create geometric faceted surfaces on your lamp. Control the number of facets, their depth, and the smoothness of blending between facets.",
    videoUrl: facetGif
  },
  layer: {
    title: "Layer Effect",
    description: "Create layered, terraced effects on your lamp. Control the number of layers, their amplitude, and add organic variation for a more natural look.",
    videoUrl: layerGif
  },
};

// Add these helper functions at the top level
const randomInRange = (min, max) => Math.random() * (max - min) + min;
const randomIntInRange = (min, max) => Math.floor(randomInRange(min, max));
const randomChoice = (array) => array[Math.floor(Math.random() * array.length)];

// Add this new component before the LampBuilder component
function ClockSync({ onClockUpdate }) {
  const lastUpdate = useRef(0);
  
  useFrame((state) => {
    // Only update clock every 100ms to reduce unnecessary updates
    const now = Date.now();
    if (now - lastUpdate.current > 100) {
      onClockUpdate(state.clock.elapsedTime);
      lastUpdate.current = now;
    }
  });
  return null;
}

// Add this new component before LampBuilder
function DynamicLighting({ renderingConfig, shadowsEnabled, lightOffset }) {
  const { camera } = useThree();
  const dirLightRef = useRef();
  useFrame(() => {
    if (camera) {
      const offsetWorld = new THREE.Vector3(lightOffset.x, lightOffset.y, lightOffset.z).applyQuaternion(camera.quaternion);
      const lightPosition = camera.position.clone().add(offsetWorld);
      renderingConfig.lighting.directionalPosition = {
        x: lightPosition.x,
        y: lightPosition.y,
        z: lightPosition.z
      };
    }
  });

  return (
    <>
      <ambientLight 
        intensity={renderingConfig.lighting.ambientIntensity} 
        color={new THREE.Color(renderingConfig.lighting.ambientColor || '#ffffff')}
      />
      <directionalLight
        ref={dirLightRef}
        position={[
          renderingConfig.lighting.directionalPosition.x,
          renderingConfig.lighting.directionalPosition.y,
          renderingConfig.lighting.directionalPosition.z
        ]}
        intensity={renderingConfig.lighting.directionalIntensity ?? 2}
        color={new THREE.Color(renderingConfig.lighting.directionalColor || '#ffffff')}
        castShadow={shadowsEnabled}
        shadow-mapSize-width={renderingConfig.shadows.mapSize}
        shadow-mapSize-height={renderingConfig.shadows.mapSize}
        shadow-bias={renderingConfig.shadows.shadowBias ?? -0.002}
      />
      <pointLight 
        position={[
          renderingConfig.lighting.pointLight1Position.x,
          renderingConfig.lighting.pointLight1Position.y,
          renderingConfig.lighting.pointLight1Position.z
        ]} 
        intensity={renderingConfig.lighting.pointLightIntensity}
        color={new THREE.Color(renderingConfig.lighting.pointLightColor || '#ffffff')}
      />
      <pointLight 
        position={[
          renderingConfig.lighting.pointLight2Position.x,
          renderingConfig.lighting.pointLight2Position.y,
          renderingConfig.lighting.pointLight2Position.z
        ]} 
        intensity={renderingConfig.lighting.pointLightIntensity}
        color={new THREE.Color(renderingConfig.lighting.pointLightColor || '#ffffff')}
      />
    </>
  );
}

// Add this after the imports
const CAMERA_CONFIG = {
  position: [0, 125, 700], // Restore original default position
  target: [0, 125, 0],
  minDistance: 400, // Keep minimum distance at 400
  maxDistance: 1000,
  fov: 30,
  far: 2000
};

const PREVIEW_CAMERA_CONFIG = {
  position: [0, 125, 350],
  target: [0, 125, 0],
  minDistance: 150,
  maxDistance: 300,
  fov: 30,
  far: 2000
};

function DesignPreview({ design }) {
  const { scene, camera } = useThree();
  const controlsRef = useRef();
  
  // Create the lamp geometry
  const geometry = useMemo(() => {
    const lampGeometry = new LampGeometry(
      design.baseShape,
      design.modifiers,
      design.noiseSeed
    );
    return lampGeometry.getGeometry();
  }, [design]);

  // Create material
  const material = useMemo(() => {
    return new THREE.MeshStandardMaterial({
      color: design.rendering?.material?.color || '#ffffff',
      roughness: design.rendering?.material?.roughness || 0.5,
      metalness: design.rendering?.material?.metalness || 0,
      side: THREE.DoubleSide
    });
  }, [design]);

  // Create mesh
  const mesh = useMemo(() => {
    return new THREE.Mesh(geometry, material);
  }, [geometry, material]);

  // Add mesh to scene
  useEffect(() => {
    scene.add(mesh);
    return () => {
      scene.remove(mesh);
      geometry.dispose();
      material.dispose();
    };
  }, [scene, mesh, geometry, material]);

  // Set up camera
  useEffect(() => {
    camera.position.set(...PREVIEW_CAMERA_CONFIG.position);
    camera.lookAt(...PREVIEW_CAMERA_CONFIG.target);
  }, [camera]);

  return (
    <>
      <OrbitControls
        ref={controlsRef}
        enableDamping={true}
        dampingFactor={0.25}
        minDistance={PREVIEW_CAMERA_CONFIG.minDistance}
        maxDistance={PREVIEW_CAMERA_CONFIG.maxDistance}
        rotateSpeed={0.5}
        enablePan={true}
        target={PREVIEW_CAMERA_CONFIG.target}
        makeDefault
      />
      <ambientLight intensity={0.5} />
      <directionalLight position={[1, 1, 1]} intensity={0.5} />
    </>
  );
}

// Modifier index enumerator for compatibility matrix
const MODIFIER_ENUM = {
  DISPLACEMENT: 0,
  TWIST: 1,
  RADIAL_WAVE: 2,
  RADIAL_WRINKLE: 3,
  FACET: 4,
  LAYER: 5,
};

// Modifier compatibility matrix (placeholder, upper triangle only)
// Use MODIFIER_ENUM for indices. Only fill [i][j] for i <= j.
const MODIFIER_COMPATIBILITY_UPPER = [
  // D   T   RW  RWk F   L
  [1,  1,  1,  1,  1,  1], // D
  [   , 1,  1,  1,  1,  1], // T
  [   ,   , 1,  1,  1,  1], // RW
  [   ,   ,   , 1,  1,  1], // RWk
  [   ,   ,   ,   , 1,  1], // F
  [   ,   ,   ,   ,   , 1], // L
];
// TODO: Fill out the actual compatibility matrix (upper triangle only)

// Helper to symmetrize the matrix
function symmetrizeMatrix(upper) {
  const n = upper.length;
  const full = Array.from({ length: n }, (_, i) => Array(n).fill(1));
  for (let i = 0; i < n; i++) {
    for (let j = i; j < n; j++) {
      full[i][j] = upper[i][j] !== undefined ? upper[i][j] : 1;
      full[j][i] = upper[i][j] !== undefined ? upper[i][j] : 1;
    }
  }
  return full;
}
const MODIFIER_COMPATIBILITY = symmetrizeMatrix(MODIFIER_COMPATIBILITY_UPPER);

// Helper to get modifier index by type
const MODIFIER_TYPE_LIST = [
  MODIFIER_TYPES.DISPLACEMENT,
  MODIFIER_TYPES.TWIST,
  MODIFIER_TYPES.RADIAL_WAVE,
  MODIFIER_TYPES.RADIAL_WRINKLE,
  MODIFIER_TYPES.FACET,
  MODIFIER_TYPES.LAYER,
];

// Helper function to check if a modifier combination is valid
function isValidModifierCombination(modifiers) {
  // Check if twist is present
  const hasTwist = modifiers.some(mod => mod.type === MODIFIER_TYPES.TWIST);
  
  if (hasTwist) {
    // If twist is present, there must be at least one asymmetrical modifier
    const hasAsymmetricalModifier = modifiers.some(mod => !mod.is_symmetrical);
    return hasAsymmetricalModifier;
  }
  
  return true; // No twist, so combination is valid
}

function areModifiersCompatible(selectedTypes) {
  for (let i = 0; i < selectedTypes.length; i++) {
    for (let j = i + 1; j < selectedTypes.length; j++) {
      const idxA = MODIFIER_ENUM[selectedTypes[i].toUpperCase()];
      const idxB = MODIFIER_ENUM[selectedTypes[j].toUpperCase()];
      if (
        idxA === undefined || idxB === undefined ||
        MODIFIER_COMPATIBILITY[idxA][idxB] === 0 ||
        MODIFIER_COMPATIBILITY[idxB][idxA] === 0
      ) {
        return false;
      }
    }
  }
  return true;
}

function LampBuilder({ openLoginModal }) {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Expose validation debug function to global scope for console access
  useEffect(() => {
    window.setValidationDebug = setValidationDebug;
    return () => {
      delete window.setValidationDebug;
    };
  }, []);
  
  
  // Add shared clock state
  const [sharedClock, setSharedClock] = useState(0);

  // Add camera state
  const [cameraPosition, setCameraPosition] = useState([0, 125, 700]);
  const [cameraTarget, setCameraTarget] = useState([0, 125, 0]);
  const [cameraDistance, setCameraDistance] = useState(700);

  // Add camera refs
  const controlsRef = useRef();
  const lastCameraUpdate = useRef(Date.now());

  // Add effect to update camera position when distance changes
  useEffect(() => {
    const direction = new THREE.Vector3(
      cameraPosition[0] - cameraTarget[0],
      cameraPosition[1] - cameraTarget[1],
      cameraPosition[2] - cameraTarget[2]
    ).normalize();

    const newPosition = [
      cameraTarget[0] + direction.x * cameraDistance,
      cameraTarget[1] + direction.y * cameraDistance,
      cameraTarget[2] + direction.z * cameraDistance
    ];

    setCameraPosition(newPosition);
  }, [cameraDistance, cameraTarget]);

  // Calculate animation phase
  const animationPhase = useMemo(() => {
    // Calculate the phase offset to ensure boxes are in sync with shader animations
    // We want the boxes to be at opacity 0.5 when the shader is at its minimum
    const phase = (sharedClock % 1) * 2 * Math.PI; // Convert to radians
    const normalizedPhase = (Math.sin(phase) + 1) / 2; // Convert to 0-1 range
    return normalizedPhase;
  }, [sharedClock]);

  // Load stored lamp design from localStorage
  const storedDesign = useMemo(() => {
    try {
      const stored = localStorage.getItem(LAMP_DESIGN_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Validate that the stored data has the required structure
        if (parsed.diameter && parsed.height && parsed.profilePoints && parsed.modifiers && parsed.renderingConfig) {
          // Check if modifiers array is empty or invalid
          if (!Array.isArray(parsed.modifiers) || parsed.modifiers.length === 0) {
            return {
              ...parsed,
              modifiers: INITIAL_STATE.modifiers
            };
          }
          return parsed;
        }
      }
    } catch (error) {
      console.warn('Error loading lamp design from localStorage:', error);
    }
    return null;
  }, []);

  const [diameter, setDiameter] = useState(storedDesign?.diameter || INITIAL_STATE.diameter);
  const [height, setHeight] = useState(storedDesign?.height || INITIAL_STATE.height);
  const [isOrbitPaused, setIsOrbitPaused] = useState(false);
  const isInitialMount = useRef(true);
  
  // Add history state
  const [history, setHistory] = useState([{
    diameter: storedDesign?.diameter || INITIAL_STATE.diameter,
    height: storedDesign?.height || INITIAL_STATE.height,
    profilePoints: storedDesign?.profilePoints || INITIAL_STATE.profilePoints,
    modifiers: storedDesign?.modifiers || INITIAL_STATE.modifiers,
    renderingConfig: storedDesign?.renderingConfig || INITIAL_STATE.renderingConfig
  }]);
  const [currentStateIndex, setCurrentStateIndex] = useState(0);
  
  // Calculate segments based on target face size
  const tubularSegments = useMemo(() => {
    return Math.max(4, Math.round(height / TARGET_FACE_SIZE));
  }, [height]);
  
  const radialSegments = useMemo(() => {
    const circumference = Math.PI * diameter;
    return Math.max(8, Math.round(circumference / TARGET_FACE_SIZE));
  }, [diameter]);

  const [profilePoints, setProfilePoints] = useState(storedDesign?.profilePoints || INITIAL_STATE.profilePoints);

  // Initialize noise with a seed and store the seed
  const [noiseSeed, setNoiseSeed] = useState(storedDesign?.noiseSeed || Math.random());
  const [noiseGen] = useState(() => {
    const random = () => (noiseSeed * 16807) % 2147483647 / 2147483647;
    
    // Improved noise functions that better match the expected behavior
    const perlin2 = (x, y) => {
      // Scale the inputs to get better noise distribution
      const nx = x * 0.1;
      const ny = y * 0.1;
      // Use multiple octaves of noise for better detail
      const n1 = Math.sin(nx * 12.9898 + ny * 78.233) * 43758.5453;
      const n2 = Math.sin(nx * 24.9898 + ny * 156.233) * 43758.5453;
      const n3 = Math.sin(nx * 48.9898 + ny * 312.233) * 43758.5453;
      // Combine the octaves with decreasing influence
      return (n1 * 0.5 + n2 * 0.25 + n3 * 0.125) % 1;
    };

    const perlin3 = (x, y, z) => {
      // Scale the inputs to get better noise distribution
      const nx = x * 0.1;
      const ny = y * 0.1;
      const nz = z * 0.1;
      // Use multiple octaves of noise for better detail
      const n1 = Math.sin(nx * 12.9898 + ny * 78.233 + nz * 37.719) * 43758.5453;
      const n2 = Math.sin(nx * 24.9898 + ny * 156.233 + nz * 75.438) * 43758.5453;
      const n3 = Math.sin(nx * 48.9898 + ny * 312.233 + nz * 150.876) * 43758.5453;
      // Combine the octaves with decreasing influence
      return (n1 * 0.5 + n2 * 0.25 + n3 * 0.125) % 1;
    };

    return { perlin2, perlin3 };
  });

  // Initialize all modifiers as inactive
  const [modifiers, setModifiers] = useState(() => {
    const storedModifiers = storedDesign?.modifiers;
    // Always ensure all modifier types are present
    if (!storedModifiers || !Array.isArray(storedModifiers)) {
      return INITIAL_STATE.modifiers;
    }
    const completeModifiers = createCompleteModifierList(storedModifiers);
    // Ensure displacement is enabled by default
    const displacementModifier = completeModifiers.find(m => m.type === MODIFIER_TYPES.DISPLACEMENT);
    if (displacementModifier) {
      displacementModifier.disabled = false;
    }
    return completeModifiers;
  });

  const [materialColor, setMaterialColor] = useState('#ffffff');
  
  // Developer mode state
  const [showDeveloperMode, setShowDeveloperMode] = useState(false);
  const [showDeveloperControls, setShowDeveloperControls] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [angleThreshold, setAngleThreshold] = useState(defaultRenderingConfig.overhang.threshold);
  const [hoveredVertex, setHoveredVertex] = useState(null);
  const [geometries, setGeometries] = useState(null);
  const [renderingConfig, setRenderingConfig] = useState(storedDesign?.renderingConfig || INITIAL_STATE.renderingConfig);
  const [showWireframe, setShowWireframe] = useState(renderingConfig.view.wireframe);
  const [activeControlGroup, setActiveControlGroup] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({
    modifiers: false, // Set modifiers section to be expanded by default
    basic: false,
    profile: false,
    developer: true // Set developer section to be collapsed by default
  });
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipTrigger, setTooltipTrigger] = useState(null);
  const [hasOverhang, setHasOverhang] = useState(false);
  const { addToCart } = useCart();
  const [footprintPoints, setFootprintPoints] = useState(DEFAULT_FOOTPRINT);
  const [hasFootprintClash, setHasFootprintClash] = useState(false);
  const [showFootprint, setShowFootprint] = useState(false);
  const [showPrintableAreaBounds, setShowPrintableAreaBounds] = useState(false);
  const [hasPrintableAreaClash, setHasPrintableAreaClash] = useState(false);
  const [showFeedbackButtons, setShowFeedbackButtons] = useState(false);
  const [showExtremeCombinations, setShowExtremeCombinations] = useState(false);
  const [extremeCombinations, setExtremeCombinations] = useState([]);
  const [feedbackNotification, setFeedbackNotification] = useState(null);
  const [name, setName] = useState('Untitled Lamp');
  const [description, setDescription] = useState('');
  const [userEmail, setUserEmail] = useState('anonymous');
  const [availableDesigns, setAvailableDesigns] = useState([]);
  const [showDesignList, setShowDesignList] = useState(false);
  const [loadingDesigns, setLoadingDesigns] = useState(false);
  const [currentDesignId, setCurrentDesignId] = useState(null);
  const [showRatedOnly, setShowRatedOnly] = useState(false);
  const [showDeveloperFeedbackButtons, setShowDeveloperFeedbackButtons] = useState(false);
  const [showMainAreaFeedbackButtons, setShowMainAreaFeedbackButtons] = useState(false);
  const { isAuthenticated, loading, logout, isAdmin } = useAuth();

  const navigate = useNavigate();
  
  // Admin mode: check if opened from admin page
  const adminOrderId = searchParams.get('adminOrderId');
  const adminModeParam = searchParams.get('adminMode');
  const itemIndex = parseInt(searchParams.get('itemIndex') || '0', 10);
  const isAdminMode = !!adminOrderId || adminModeParam === 'true';
  const [editingEnabled, setEditingEnabled] = useState(false);
  const [loadingOrderDesign, setLoadingOrderDesign] = useState(false);
  // 1. Add state for shadow toggle
  const [shadowsEnabled, setShadowsEnabled] = useState(renderingConfig.shadows.enabled);
  // Add state for light offset tuning in LampBuilder
  const [lightOffset, setLightOffset] = useState(new THREE.Vector3(381, -58, 109));
  // Add state for show cost toggle
  const [showCost, setShowCost] = useState(true);

  const toggleCollapse = (groupName) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  // Update the showDebug state to use renderingConfig.overhang.enabled
  useEffect(() => {
    setShowDebug(renderingConfig.overhang.enabled);
  }, [renderingConfig.overhang.enabled]);

  // Update angleThreshold when overhang threshold changes
  useEffect(() => {
    setAngleThreshold(renderingConfig.overhang.threshold);
  }, [renderingConfig.overhang.threshold]);

  // Update useEffect to handle view settings from config
  useEffect(() => {
    if (renderingConfig.view) {
      setShowWireframe(renderingConfig.view.wireframe);
    }
  }, [renderingConfig.view]);

  // Add this new effect to keep renderingConfig in sync with showWireframe
  useEffect(() => {
    updateRenderingConfig('view.wireframe', showWireframe);
  }, [showWireframe]);

  // Load design from order when opened from admin page
  useEffect(() => {
    if (adminOrderId) {
      setLoadingOrderDesign(true);
      const token = localStorage.getItem('token');
      
      fetch(`${import.meta.env.VITE_API_URL}/api/orders/admin/${adminOrderId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
        .then(res => {
          if (!res.ok) throw new Error('Failed to fetch order');
          return res.json();
        })
        .then(order => {
          const item = order.items?.[itemIndex];
          if (!item) {
            throw new Error('Order item not found');
          }

          // Extract design data - support both item.design and item.baseShape/item.modifiers structure
          const design = item.design || {
            baseShape: item.baseShape,
            modifiers: item.modifiers,
            rendering: item.rendering,
            noiseSeed: item.noiseSeed
          };

          if (design.baseShape) {
            setDiameter(design.baseShape.diameter || INITIAL_STATE.diameter);
            setHeight(design.baseShape.height || INITIAL_STATE.height);
            setProfilePoints(design.baseShape.profilePoints || INITIAL_STATE.profilePoints);
          }

          if (design.modifiers) {
            const completeModifiers = createCompleteModifierList(design.modifiers);
            setModifiers(completeModifiers);
          }

          if (design.rendering) {
            setRenderingConfig(design.rendering);
            setShowWireframe(design.rendering.view?.wireframe || false);
          }

          if (design.noiseSeed !== undefined) {
            setNoiseSeed(design.noiseSeed);
          }

          setLoadingOrderDesign(false);
        })
        .catch(error => {
          console.error('Error loading order design:', error);
          toast.error('Failed to load design from order');
          setLoadingOrderDesign(false);
        });
    }
  }, [adminOrderId, itemIndex]);

  // Helper function to update nested state
  const updateRenderingConfig = (path, value) => {
    setRenderingConfig(prev => {
      const newState = JSON.parse(JSON.stringify(prev)); // Deep clone
      const parts = path.split('.');
      let current = newState;
      for (let i = 0; i < parts.length - 1; i++) {
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = value;

      return newState;
    });
  };

  const handleAddModifier = (type) => {
    const config = MODIFIER_CONFIGS[type];
    const newModifier = {
      type,
      disabled: false,
      is_symmetrical: config.is_symmetrical,
      parameters: Object.fromEntries(
        Object.entries(config.parameters).map(([key, value]) => [key, value.default])
      )
    };
    setModifiers(prev => createCompleteModifierList([...prev.filter(m => m.type !== type), newModifier]));
  };

  const handleUpdateModifier = (index, updatedModifier) => {
    setModifiers(prev => {
      const newModifiers = [...prev];
      newModifiers[index] = {
        ...updatedModifier,
        parameters: { ...updatedModifier.parameters }
      };
      return createCompleteModifierList(newModifiers);
    });
  };

  const handleRemoveModifier = (index) => {
    setModifiers(prev => {
      const newModifiers = [...prev];
      // Instead of removing, just disable and reset to default
      const type = newModifiers[index].type;
      const config = MODIFIER_CONFIGS[type];
      newModifiers[index] = {
        type,
        disabled: true,
        is_symmetrical: config.is_symmetrical,
        parameters: Object.fromEntries(
          Object.entries(config.parameters).map(([key, value]) => [key, value.default])
        )
      };
      return createCompleteModifierList(newModifiers);
    });
  };

  const handleGeometriesUpdate = useCallback((newGeometries) => {
    setGeometries(newGeometries);
    if (newGeometries.geometry) {
      const { hasFootprintClash, hasOverhang, hasPrintableAreaClash } = validateLampGeometry(
        newGeometries.geometry,
        { overhangThreshold: angleThreshold, height }
      );
      setHasFootprintClash(hasFootprintClash);
      setHasOverhang(hasOverhang);
      setHasPrintableAreaClash(hasPrintableAreaClash);
    }
  }, [height, angleThreshold]);

  const handleDownloadSTLLocal = () => {
    try {
      // Create the design object
      const design = {
        baseShape: {
          diameter,
          height,
          tubularSegments,
          radialSegments,
          profilePoints
        },
        modifiers,
        noiseSeed
      };

      // Use lamp-core's LampGeometry to generate the geometry
      const geometry = new LampGeometry(design.baseShape, design.modifiers, design.noiseSeed);
      const threeGeometry = geometry.getGeometry();

      // Create a mesh (required for STL export)
      const mesh = new THREE.Mesh(threeGeometry);

      // Export to STL
      const exporter = new STLExporter();
      const stlData = exporter.parse(mesh, { binary: true });

      // Create a blob and download
      const blob = new Blob([stlData.buffer], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'lamp.stl';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Clean up
      geometry.dispose();
      threeGeometry.dispose();
      mesh.geometry.dispose();
      mesh.material.dispose();
    } catch (error) {
      console.error('Error generating STL:', error);
      alert('Error generating STL file. Please try again.');
    }
  };

  const handleDownloadSTLLambda = async () => {
    try {
      // Create the design object
      const design = {
        baseShape: {
          diameter,
          height,
          tubularSegments,
          radialSegments,
          profilePoints
        },
        modifiers,
        noiseSeed
      };

      console.log('Sending request to:', `${import.meta.env.VITE_API_URL}/mesh`);
      console.log('Request payload:', design);

      // Call the Lambda function
      const response = await fetch(`${import.meta.env.VITE_API_URL}/mesh`, {
        method: 'POST',
        body: JSON.stringify(design),
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': import.meta.env.VITE_API_KEY
        }
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to generate STL: ${response.status} ${errorText}`);
      }

      // Get the STL file
      const blob = await response.blob();
      console.log('Received STL blob:', blob.size, 'bytes');
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `lamp_${diameter}x${height}mm.stl`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting STL via Lambda:', error);
      alert('Unable to export STL via Lambda. Please try again later.');
    }
  };

  const handleTooltipShow = (menuItem, event) => {
    // Find the closest h3 element and get the rect from it
    const headerElement = event.currentTarget.closest('h3');
    const rect = headerElement.getBoundingClientRect();
    
    // Calculate initial position - align right edge of tooltip with left edge of canvas
    // Canvas starts at window.innerWidth - 350px, so tooltip right edge should be at that position
    const tooltipWidth = 240; // Width from CSS
    const canvasLeftEdge = window.innerWidth - 350; // Canvas starts here
    let x = canvasLeftEdge - tooltipWidth - 10; // Position tooltip 10px to the left of canvas edge
    let y = rect.top + window.scrollY; // Align top of tooltip with top of h3 background

    // Get viewport dimensions
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    // Conservative tooltip dimensions - account for scrollable content
    const tooltipMaxHeight = viewportHeight - 40; // From CSS: calc(100vh - 20px) + margin
    const tooltipMinHeight = 350; // Minimum height for content (header + description + video/gif)
    
    // Use the smaller of max height or minimum content height
    const tooltipHeight = Math.min(tooltipMaxHeight, tooltipMinHeight);

    // Debug logging
    console.log('Tooltip positioning debug:', {
      triggerRect: { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right },
      viewport: { height: viewportHeight, width: viewportWidth },
      canvasLeftEdge: canvasLeftEdge,
      scroll: { x: window.scrollX, y: window.scrollY },
      tooltip: { width: tooltipWidth, height: tooltipHeight, maxHeight: tooltipMaxHeight, minHeight: tooltipMinHeight },
      initialPosition: { x, y }
    });

    // Check if tooltip would go below viewport
    if (y + tooltipHeight > viewportHeight + window.scrollY - 30) {
      // Position tooltip above the trigger point
      y = rect.bottom + window.scrollY - tooltipHeight - 30;
      console.log('Repositioning tooltip above trigger. New y:', y);
    }

    // Ensure tooltip doesn't go above viewport
    y = Math.max(window.scrollY + 30, y);
    
    // Check if tooltip would go off-screen to the left
    if (x < 30) {
      // Position tooltip to the right of the button instead
      x = rect.right + window.scrollX + 30;
      console.log('Repositioning tooltip to the right. New x:', x);
    }
    
    // Ensure tooltip doesn't go off-screen to the right
    if (x + tooltipWidth > viewportWidth - 30) {
      x = viewportWidth - tooltipWidth - 30;
      console.log('Repositioning tooltip to fit on screen. New x:', x);
    }
    
    // Final safety check - ensure tooltip is within viewport
    if (y + tooltipHeight > viewportHeight + window.scrollY) {
      y = viewportHeight + window.scrollY - tooltipHeight - 30;
      console.log('Final safety check - repositioning tooltip. New y:', y);
    }
    
    console.log('Final tooltip position:', { x, y });
    
    setTooltipPosition({ x, y });
    setTooltipTrigger(event.currentTarget);
    setActiveTooltip(menuItem);
  };

  const handleTooltipHide = () => {
    setActiveTooltip(null);
    setTooltipTrigger(null);
  };

  const handleSaveLamp = async () => {
    try {
      const lampDesign = {
        version: '1.0.0',
        metadata: {
          name: 'Untitled Lamp',
          description: '',
          createdAt: new Date().toISOString(),
          lastModified: new Date().toISOString(),
          targetFaceSize: TARGET_FACE_SIZE,
          scaleFactor: SCALE_FACTOR,
          maxDimension: MAX_DIMENSION,
          modifierTypes: Object.keys(MODIFIER_TYPES),
          modifierConfigs: MODIFIER_CONFIGS
        },
        baseShape: {
          diameter,
          height,
          tubularSegments,
          radialSegments,
          profilePoints
        },
        modifiers,
        rendering: renderingConfig,
        noiseSeed
      };

      validateLampDesign(lampDesign);
      const jsonString = await serializeLampDesign(lampDesign);
      
      // Create download link
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `lamp_${diameter}x${height}mm.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error saving lamp design:', error);
      alert('Unable to save lamp design: ' + error.message);
    }
  };

  const handleLoadLamp = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const lampDesign = deserializeLampDesign(e.target.result);
        // Update state with loaded design
        setDiameter(lampDesign.baseShape.diameter);
        setHeight(lampDesign.baseShape.height);
        setProfilePoints(lampDesign.baseShape.profilePoints);
        setModifiers(createCompleteModifierList(lampDesign.modifiers));
        setRenderingConfig(lampDesign.rendering);
        // Recreate noise generator with the loaded seed
        if (lampDesign.noiseSeed) {
          noiseGen.seed(lampDesign.noiseSeed);
        }
      } catch (error) {
        console.error('Error loading lamp design:', error);
        alert('Unable to load lamp design: ' + error.message);
      }
    };
    reader.readAsText(file);
  };

  const handleFeedback = async (isPositive) => {
    try {
      // Always create a new design with a new ID when rating
      const design = {
        baseShape: {
          diameter,
          height,
          tubularSegments,
          radialSegments,
          profilePoints
        },
        modifiers,
        rendering: renderingConfig,
        noiseSeed
      };

      const savedDesign = await saveDesignWithRating(design, isPositive, userEmail);
      setCurrentDesignId(savedDesign.id);

      setFeedbackNotification({
        type: 'success',
        message: 'Thank you for your feedback!'
      });

      // Clear notification after 3 seconds
      setTimeout(() => {
        setFeedbackNotification(null);
      }, 3000);
    } catch (error) {
      console.error('Error saving feedback:', error);
      setFeedbackNotification({
        type: 'error',
        message: 'Failed to save feedback. Please try again.'
      });
    }
  };

  // Add this helper inside LampBuilder
  const waitForNextTick = () => new Promise(resolve => setTimeout(resolve, 0));

  const handleRandomiseLamp = async (showFeedback = false) => {
    try {
      const lampDesign = await generateRandomLamp({
        LAMP_CONFIG,
        MODIFIER_CONFIGS,
        MODIFIER_TYPES,
        TARGET_FACE_SIZE,
        angleThreshold,
        LampGeometry,
        validateLampGeometry,
        generateAestheticCompliantColorPair,
        defaultRenderingConfig,
        renderingConfig,
        MODIFIER_RETRY_CONFIG
      });
      // Update state with the generated lamp design
      setDiameter(lampDesign.baseShape.diameter);
      setHeight(lampDesign.baseShape.height);
      setProfilePoints(lampDesign.baseShape.profilePoints);
      setModifiers(createCompleteModifierList(lampDesign.modifiers));
      setRenderingConfig(lampDesign.rendering);
      setNoiseSeed(lampDesign.noiseSeed);
      setIsOrbitPaused(false);
      setCurrentDesignId(null);
      setShowDeveloperFeedbackButtons(true);
      setShowMainAreaFeedbackButtons(false);
      try {
        localStorage.removeItem(LAMP_DESIGN_STORAGE_KEY);
      } catch (error) {
        console.warn('Error clearing lamp design from localStorage:', error);
      }
    } catch (error) {
      alert('Unable to generate a valid random design. Please try again.');
    }
  };

  const handleNewLamp = () => {
    // Clear the current design ID to ensure next save creates a new design
    setCurrentDesignId(null);
    // Reset to initial state
    setDiameter(INITIAL_STATE.diameter);
    setHeight(INITIAL_STATE.height);
    setProfilePoints(INITIAL_STATE.profilePoints);
    setModifiers(INITIAL_STATE.modifiers);
    setRenderingConfig(INITIAL_STATE.renderingConfig);
    // Reset history
    setHistory([{
      diameter: INITIAL_STATE.diameter,
      height: INITIAL_STATE.height,
      profilePoints: INITIAL_STATE.profilePoints,
      modifiers: INITIAL_STATE.modifiers,
      renderingConfig: INITIAL_STATE.renderingConfig
    }]);
    setCurrentStateIndex(0);
    // Reset noise generator with a new seed
    const newSeed = Math.random();
    setNoiseSeed(newSeed);
    // Clear localStorage to reset stored design
    try {
      localStorage.removeItem(LAMP_DESIGN_STORAGE_KEY);
    } catch (error) {
      console.warn('Error clearing lamp design from localStorage:', error);
    }
    toast.success('New lamp design started!');
  };

  // Add saveToHistory function
  const saveToHistory = useCallback(() => {
    const currentState = {
      diameter,
      height,
      profilePoints,
      modifiers,
      renderingConfig
    };

    // Only save if the state has actually changed
    const lastState = history[currentStateIndex];
    const hasChanged = JSON.stringify(lastState) !== JSON.stringify(currentState);

    if (!hasChanged) {
      return;
    }

    // Remove any future states if we're not at the end of history
    const newHistory = history.slice(0, currentStateIndex + 1);
    
    // Add current state to history
    setHistory([...newHistory, currentState]);
    setCurrentStateIndex(newHistory.length);
  }, [diameter, height, profilePoints, modifiers, renderingConfig, history, currentStateIndex]);

  // Add effect to save state changes
  useEffect(() => {
    // Only skip the very first render when the component mounts
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    // Debounce the save to avoid too many history entries
    const timeoutId = setTimeout(() => {
      saveToHistory();
    }, 500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [diameter, height, profilePoints, modifiers, renderingConfig, saveToHistory]);

  // Add handleUndo function
  const handleUndo = useCallback(() => {
    if (currentStateIndex > 0) {
      const newIndex = currentStateIndex - 1;
      const previousState = history[newIndex];
      
      setDiameter(previousState.diameter);
      setHeight(previousState.height);
      setProfilePoints(previousState.profilePoints);
      setModifiers(previousState.modifiers);
      setRenderingConfig(previousState.renderingConfig);
      setCurrentStateIndex(newIndex);
    }
  }, [currentStateIndex, history]);

  // Add handleRedo function
  const handleRedo = useCallback(() => {
    if (currentStateIndex < history.length - 1) {
      const newIndex = currentStateIndex + 1;
      const nextState = history[newIndex];
      
      setDiameter(nextState.diameter);
      setHeight(nextState.height);
      setProfilePoints(nextState.profilePoints);
      setModifiers(nextState.modifiers);
      setRenderingConfig(nextState.renderingConfig);
      setCurrentStateIndex(newIndex);
    }
  }, [currentStateIndex, history]);


  const handleAddToCart = async () => {
    const lampId = `custom-lamp-${Date.now()}`;
    
    // Create design object for thumbnail generation
    const design = {
      id: lampId,
      baseShape: { 
        diameter, 
        height,
        tubularSegments,
        radialSegments,
        profilePoints
      },
      modifiers: modifiers,
      rendering: renderingConfig,
      noiseSeed: noiseSeed
    };

    // Generate thumbnail for email
    let thumbnail = null;
    try {
      thumbnail = await generateCachedLampThumbnail(design, {
        maxDimension: 512,
        format: 'image/jpeg',
        quality: 0.85
      });
      if (thumbnail) {
        console.log('Thumbnail generated successfully, length:', thumbnail.length);
      } else {
        console.warn('Thumbnail generation returned null/undefined');
      }
    } catch (error) {
      console.error('Failed to generate thumbnail:', error);
      // Continue without thumbnail - cart will still work
    }

    const lampProduct = {
      id: lampId,
      name: `Custom Lamp ${diameter}x${height}mm`,
      price: '$90 AUD',
      description: 'Your custom designed lamp',
      specifications: {
        dimensions: `${diameter}mm x ${height}mm`,
        material: 'recycled aluminum',
        color: renderingConfig.material.color
      },
      // Include thumbnail for email confirmation
      thumbnail: thumbnail,
      // Include complete design data for rendering in cart
      baseShape: { 
        diameter, 
        height,
        tubularSegments,
        radialSegments,
        profilePoints
      },
      modifiers: modifiers,
      rendering: renderingConfig,
      noiseSeed: noiseSeed
    };
    addToCart(lampProduct);
    toast.success('Added to cart!');
  };

  // Update the clashMaterial shader
  const clashMaterial = useMemo(() => {
    const material = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        clashOpacity: { value: renderingConfig.clash.opacity || 0.5 },
        clashColor: { value: new THREE.Color(renderingConfig.clash.color || '#0088ff') }
      },
      vertexShader: `
        varying vec3 vPosition;
        void main() {
          vPosition = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float time;
        uniform float clashOpacity;
        uniform vec3 clashColor;
        varying vec3 vPosition;
        
        void main() {
          float pulse = (sin(time * 3.14159) * 0.5 + 0.5);
          float opacity = mix(0.2 * clashOpacity, clashOpacity, pulse);
          
          float dist = sqrt(vPosition.x * vPosition.x + vPosition.z * vPosition.z);
          
          // Skip clash detection for bottom cap and its extrusion (y <= 0)
          const float EPSILON = 0.001;
          if (vPosition.y <= EPSILON) {
            discard;
          }
          
          // Check for clashes in both the light base and light globe areas
          if ((dist <= 45.0 && vPosition.y <= 10.0) || // Light base clash (90mm diameter, 10mm height)
              (dist <= 15.0 && vPosition.y <= 200.0)) { // Light globe clash (30mm diameter, 200mm height)
            gl_FragColor = vec4(clashColor, opacity);
          } else {
            discard;
          }
        }
      `,
      transparent: true,
      side: THREE.DoubleSide,
      depthTest: true,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -4
    });

    return material;
  }, [renderingConfig.clash.color, renderingConfig.clash.opacity]);

  // Update the angleMaterial shader
  const angleMaterial = useMemo(() => {
    const material = new THREE.ShaderMaterial({
      uniforms: {
        angleThreshold: { value: angleThreshold * Math.PI / 180 },
        overhangOpacity: { value: renderingConfig.overhang.opacity },
        overhangColor: { value: new THREE.Color(renderingConfig.overhang.color || '#ff0000') },
        time: { value: 0 }
      },
      vertexShader: `
        varying vec3 vNormal;
        varying vec3 vPosition;
        void main() {
          vNormal = normalize(normal);
          vPosition = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float angleThreshold;
        uniform float overhangOpacity;
        uniform vec3 overhangColor;
        uniform float time;
        varying vec3 vNormal;
        varying vec3 vPosition;
        
        void main() {
          // Skip overhang check for bottom cap and its extrusion (y <= 0)
          const float EPSILON = 0.001;
          if (vPosition.y <= EPSILON) {
            discard;
          }

          float angle = acos(abs(dot(normalize(vNormal), vec3(0.0, 1.0, 0.0))));
          float overhangAngle = (3.14159 / 2.0) - angle;
          
          if (overhangAngle > angleThreshold) {
            float pulse = (sin(time * 3.14159) * 0.5 + 0.5);
            float finalOpacity = mix(0.2 * overhangOpacity, overhangOpacity, pulse);
            gl_FragColor = vec4(overhangColor, finalOpacity);
          } else {
            discard;
          }
        }
      `,
      transparent: true,
      side: THREE.DoubleSide,
      depthTest: true,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -4
    });

    return material;
  }, [angleThreshold, renderingConfig.overhang.opacity, renderingConfig.overhang.color]);

  // Update uniforms with shared clock
  useEffect(() => {
    if (clashMaterial) {
      clashMaterial.uniforms.time.value = sharedClock;
    }
    if (angleMaterial) {
      angleMaterial.uniforms.time.value = sharedClock;
    }
  }, [sharedClock, clashMaterial, angleMaterial]);

  // Add effect to handle orbit controls state
  useEffect(() => {
    if (controlsRef.current) {
      // Only control auto-rotation, keep manual rotation enabled
      controlsRef.current.autoRotate = !isOrbitPaused;
      controlsRef.current.enableRotate = true;
      controlsRef.current.enablePan = true;
    }
  }, [isOrbitPaused]);

  // Load extreme combinations
  useEffect(() => {
    setExtremeCombinations(getExtremeCombinations());
  }, []);

  // Add this handler in the LampBuilder component
  const handleSaveLampToDB = async () => {
    if (loading) return;
    const token = localStorage.getItem('token');
    const isAuth = isAuthenticated && token && !isTokenExpired(token);
    
    // For authenticated users, check token validity
    if (isAuth && (!token || isTokenExpired(token))) {
      await logout();
      openLoginModal('required');
      return;
    }
    
    try {
      // Get the canvas element
      const canvas = document.querySelector('canvas');
      if (!canvas) {
        throw new Error('Canvas element not found');
      }
      // Create a temporary canvas for the thumbnail
      const thumbnailCanvas = document.createElement('canvas');
      const thumbnailSize = 256; // 256x256 thumbnail
      thumbnailCanvas.width = thumbnailSize;
      thumbnailCanvas.height = thumbnailSize;
      const ctx = thumbnailCanvas.getContext('2d');
      ctx.drawImage(canvas, 0, 0, thumbnailSize, thumbnailSize);
      const thumbnail = thumbnailCanvas.toDataURL('image/jpeg', 0.8);
      const lampDesign = {
        name: name,
        description: description,
        createdBy: isAuth ? userEmail : 'guest',
        version: 1,
        status: 'draft',
        isRandomized: true, // Mark as randomized since it's from the random generator
        baseShape: {
          diameter,
          height,
          tubularSegments,
          radialSegments,
          profilePoints
        },
        modifiers,
        noiseSeed,
        thumbnail // Add the thumbnail to the design
      };
      
      // Get cacheId for guest users
      const cacheId = isAuth ? undefined : getOrCreateCacheId();
      
      // Use the API function which handles both authenticated and guest users
      const savedDesign = await saveLampDesign(lampDesign, cacheId);
      setCurrentDesignId(savedDesign.id);
      toast.success('Lamp design saved to database!');
    } catch (err) {
      if (err.message && err.message.includes('401')) {
        await logout();
        openLoginModal('required');
        return;
      }
      toast.error('Failed to save lamp design: ' + (err.message || err));
    }
  };

  const handleLoadFromDB = async () => {
    if (loading) return;
    const token = localStorage.getItem('token');
    const isAuth = isAuthenticated && token && !isTokenExpired(token);
    
    try {
      setLoadingDesigns(true);
      let response;
      
      if (isAuth) {
        // Authenticated user: use the authenticated endpoint
        response = await fetch(`${API_BASE_URL}/api/lamp-designs/user/my-designs`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        if (response.status === 401) {
          await logout();
          openLoginModal('required');
          return;
        }
      } else {
        // Guest user: use the guest endpoint with cacheId
        const cacheId = getOrCreateCacheId();
        response = await fetch(`${API_BASE_URL}/api/lamp-designs/guest/my-designs?cacheId=${cacheId}`);
      }
      
      if (!response.ok) {
        if (response.status === 400) {
          throw new Error('Invalid request. Please try again.');
        }
        throw new Error('Failed to fetch your designs');
      }
      
      const designs = await response.json();
      
      // Create a single renderer for all previews
      const previewCanvas = document.createElement('canvas');
      const previewSize = 512; // 512x512 preview for higher quality
      previewCanvas.width = previewSize;
      previewCanvas.height = previewSize;
      const renderer = new THREE.WebGLRenderer({ canvas: previewCanvas, antialias: true, powerPreference: 'high-performance', preserveDrawingBuffer: true });
      renderer.setSize(previewSize, previewSize);
      renderer.outputEncoding = THREE.sRGBEncoding;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.setClearColor(0xf5f5f5, 1);
      
      // Create a single scene and camera
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(
        PREVIEW_CAMERA_CONFIG.fov,
        1, // aspect ratio is 1 for square preview
        PREVIEW_CAMERA_CONFIG.minDistance,
        PREVIEW_CAMERA_CONFIG.far
      );
      // Double the distance from the camera position
      const previewPosition = [
        PREVIEW_CAMERA_CONFIG.position[0] * 2,
        PREVIEW_CAMERA_CONFIG.position[1],
        PREVIEW_CAMERA_CONFIG.position[2] * 2
      ];
      camera.position.set(...previewPosition);
      camera.lookAt(...PREVIEW_CAMERA_CONFIG.target);
      
      // Add high-quality lights to the scene
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
      scene.add(ambientLight);
      const directionalLight = new THREE.DirectionalLight(0xffffff, 1.2);
      directionalLight.position.set(1, 2, 2);
      scene.add(directionalLight);
      // Add a fill light
      const fillLight = new THREE.DirectionalLight(0xffffff, 0.5);
      fillLight.position.set(-2, 1, 1);
      scene.add(fillLight);
      // Add a rim light
      const rimLight = new THREE.DirectionalLight(0xffffff, 0.4);
      rimLight.position.set(0, 2, -2);
      scene.add(rimLight);
      
      // Create previews for each design
      const designsWithPreviews = await Promise.all(designs.map(async (design) => {
        const preview = await generateCachedLampThumbnail(design, {
          maxDimension: 512,
          enableDebugLogging: false
        });
        return {
          ...design,
          preview
        };
      }));
      
      // Clean up the renderer
      renderer.dispose();
      
      // Sort designs by creation date (newest first)
      const sortedDesigns = designsWithPreviews.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      setAvailableDesigns(sortedDesigns);
      setShowDesignList(true);
    } catch (error) {
      console.error('Error loading designs:', error);
      alert('Failed to load designs from database: ' + error.message);
    } finally {
      setLoadingDesigns(false);
    }
  };

  const handleSelectDesign = async (designId) => {
    try {
      const token = localStorage.getItem('token');
      let design;
      
      // If in admin mode, use admin endpoint
      if (isAdminMode) {
        const response = await fetch(`${API_BASE_URL}/api/lamp-designs/admin/${designId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch design');
        }
        design = await response.json();
      } else {
        // Normal flow: Get cacheId for guest users
        const isAuth = isAuthenticated && token && !isTokenExpired(token);
        const cacheId = isAuth ? undefined : getOrCreateCacheId();
        design = await getLampDesign(designId, cacheId);
      }
      
      setCurrentDesignId(designId);
      
      // Update state with loaded design
      setDiameter(design.baseShape.diameter);
      setHeight(design.baseShape.height);
      setProfilePoints(design.baseShape.profilePoints);
      setModifiers(createCompleteModifierList(design.modifiers));
      if (design.rendering) {
        setRenderingConfig(design.rendering);
      }
      
      // Recreate noise generator with the loaded seed
      if (design.noiseSeed) {
        setNoiseSeed(design.noiseSeed);
        // The noiseGen will be recreated in the useEffect that depends on noiseSeed
      }

      // Show feedback buttons if this is a randomized design
      setShowDeveloperFeedbackButtons(design.isRandomized || false);

      setShowDesignList(false);
    } catch (error) {
      console.error('Error loading design:', error);
      alert('Failed to load design: ' + error.message);
    }
  };

  const handleDeleteDesign = async (designId, event) => {
    event?.stopPropagation();
    if (window.confirm('Are you sure you want to delete this design?')) {
      try {
        // Get cacheId for guest users
        const token = localStorage.getItem('token');
        const isAuth = isAuthenticated && token && !isTokenExpired(token);
        const cacheId = isAuth ? undefined : getOrCreateCacheId();
        
        await deleteLampDesign(designId, cacheId);
        setAvailableDesigns(designs => designs.filter(d => d.id !== designId));
        // Remove cached thumbnail
        localStorage.removeItem(`lamp_thumbnail_${designId}`);
        toast.success('Design deleted successfully');
      } catch (error) {
        console.error('Error deleting design:', error);
        toast.error('Failed to delete design: ' + error.message);
      }
    }
  };

  // Load design from URL params if designId is present
  useEffect(() => {
    const designId = searchParams.get('designId');
    const adminModeParam = searchParams.get('adminMode');
    if (designId && designId !== currentDesignId) {
      // If opened from admin, set admin mode
      if (adminModeParam === 'true' && !adminOrderId) {
        // Set admin mode for design opened from admin (but not from order)
        // This is handled by the adminOrderId check above
      }
      handleSelectDesign(designId).catch(error => {
        console.error('Error loading design from URL:', error);
        toast.error('Failed to load design from URL: ' + error.message);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Update the main canvas camera settings
  useEffect(() => {
    if (controlsRef.current) {
      controlsRef.current.minDistance = CAMERA_CONFIG.minDistance;
      controlsRef.current.maxDistance = CAMERA_CONFIG.maxDistance;
      controlsRef.current.target.set(...CAMERA_CONFIG.target);
    }
  }, [controlsRef]);

  // Save lamp design to localStorage on state change
  useEffect(() => {
    // Skip saving on initial mount to avoid overwriting with default values
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    try {
      const lampState = {
        diameter,
        height,
        profilePoints,
        modifiers,
        renderingConfig,
        noiseSeed
      };
      localStorage.setItem(LAMP_DESIGN_STORAGE_KEY, JSON.stringify(lampState));
      //console.log('Saved lamp design to localStorage:', lampState);
    } catch (error) {
      console.warn('Error saving lamp design to localStorage:', error);
    }
  }, [diameter, height, profilePoints, modifiers, renderingConfig, noiseSeed]);

  // 1. In LampBuilder, log the value of shadowsEnabled each render
  useEffect(() => {
    console.log('[LampBuilder] shadowsEnabled:', shadowsEnabled);
  }, [shadowsEnabled]);

  // Handler for slider changes
  const handleLightOffsetChange = (axis, value) => {
    setLightOffset((prev) => ({ ...prev, [axis]: value }));
  };

  // Handler for copying code snippet
  const handleCopyOffsetCode = () => {
    const code = `new THREE.Vector3(${lightOffset.x}, ${lightOffset.y}, ${lightOffset.z})`;
    navigator.clipboard.writeText(code);
    alert(
      'Copied to clipboard: ' + code + '\n\nPaste this code into the default value for the lightOffset state in LampBuilder.jsx (search for useState({ x: ..., y: ..., z: ... })) to make it permanent.'
    );
  };

  // Utility function to check if a JWT is expired
  function isTokenExpired(token) {
    if (!token) return true;
    try {
      const [, payload] = token.split('.');
      const decoded = JSON.parse(atob(payload));
      if (!decoded.exp) return true;
      return Date.now() >= decoded.exp * 1000;
    } catch (e) {
      return true;
    }
  }

  // --- Cost Estimator State ---
  const [costEstimate, setCostEstimate] = useState({ area: 0, cost: 0 });
  const lastDesignRef = useRef(null);
  const lastUpdateRef = useRef(Date.now());
  const updateTimeoutRef = useRef(null);

  // Helper to get current design as LampDesign
  const getCurrentDesign = useCallback(() => {
    return {
      id: 'temp',
      name,
      description,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: userEmail,
      version: 1,
      status: 'draft',
      baseShape: {
        diameter,
        height,
        tubularSegments,
        radialSegments,
        profilePoints,
      },
      modifiers,
      noiseSeed,
      rendering: renderingConfig,
      metadata: {
        name,
        description,
        createdAt: new Date().toISOString(),
        lastModified: new Date().toISOString(),
      },
    };
  }, [name, description, userEmail, diameter, height, tubularSegments, radialSegments, profilePoints, modifiers, noiseSeed, renderingConfig]);

  // Cost update effect: on geometry change or every 1s (whichever is less frequent)
  useEffect(() => {
    const updateCost = () => {
      const design = getCurrentDesign();
      const designStr = JSON.stringify(design);
      // Only update if at least 1 second has passed since the last update
      if (Date.now() - lastUpdateRef.current > 300) {
        try {
          const result = estimateSurfaceAreaFromDesign(design);
          setCostEstimate(result);
        } catch (e) {
          setCostEstimate({ area: 0, cost: 0 });
        }
        lastDesignRef.current = designStr;
        lastUpdateRef.current = Date.now();
      }
    };
    updateCost();
    if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current);
    updateTimeoutRef.current = setTimeout(updateCost, 1000);
    return () => {
      if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current);
    };
  }, [getCurrentDesign]);

  return (
    <>
      <div className="builder-container">
        <style>
          {`
            @keyframes pulse {
              0% { opacity: 0.5; }
              50% { opacity: 1; }
              100% { opacity: 0.5; }
            }
            .warning-box {
              animation: pulse 3s infinite; /* Changed from default 2s to 3s */
              opacity: ${0.5 + (animationPhase * 0.5)};
              transition: opacity 0.016s linear;
            }
          `}
        </style>
        <div className="canvas-container">
          <Canvas 
            shadows={true} 
            camera={{ 
              position: CAMERA_CONFIG.position,
              fov: CAMERA_CONFIG.fov,
              near: 0.1,
              far: CAMERA_CONFIG.far
            }}
            style={{ background: renderingConfig.background.color }}
            dpr={[1, 2]}
            gl={{ 
              antialias: true,
              alpha: true,
              preserveDrawingBuffer: true,
              powerPreference: "high-performance",
            }}
            onCreated={({ camera }) => {
              camera.lookAt(...CAMERA_CONFIG.target);
            }}
          >
            <ClockSync onClockUpdate={setSharedClock} />
            <DynamicLighting renderingConfig={renderingConfig} shadowsEnabled={shadowsEnabled} lightOffset={lightOffset} />
            <group position={[0, 0, 0]}>
              <Lamp 
                diameter={diameter} 
                height={height}
                tubularSegments={tubularSegments}
                radialSegments={radialSegments}
                showWireframe={showWireframe}
                showVertices={showWireframe}
                showDebug={showDebug}
                onGeometriesUpdate={handleGeometriesUpdate}
                noiseGen={noiseGen}
                modifiers={modifiers}
                profilePoints={profilePoints}
                angleThreshold={angleThreshold}
                renderingConfig={renderingConfig}
                onOverhangChange={setHasOverhang}
                showFootprint={showFootprint}
                showPrintableAreaBounds={showPrintableAreaBounds}
                sharedClock={sharedClock}
                hasPrintableAreaClash={hasPrintableAreaClash}
                shadowsEnabled={shadowsEnabled}
              />
              <OrbitControls 
                ref={controlsRef}
                enableDamping={true}
                dampingFactor={0.25}
                minDistance={CAMERA_CONFIG.minDistance}
                maxDistance={CAMERA_CONFIG.maxDistance}
                rotateSpeed={0.5}
                enablePan={true}
                target={CAMERA_CONFIG.target}
                makeDefault
                autoRotate={!isOrbitPaused}
                autoRotateSpeed={1}
                onChange={(e) => {
                  // Skip updates if less than 100ms has passed since last update
                  const now = Date.now();
                  if (now - lastCameraUpdate.current < 100) return;
                  
                  // Update camera position and target when controls change
                  const camera = e.target.object;
                  const newPosition = [camera.position.x, camera.position.y, camera.position.z];
                  const newTarget = [e.target.target.x, e.target.target.y, e.target.target.z];
                  
                  setCameraPosition(newPosition);
                  setCameraTarget(newTarget);
                  setCameraDistance(camera.position.distanceTo(e.target.target));
                  lastCameraUpdate.current = now;
                }}
              />
            </group>
          </Canvas>
          <div style={{
            position: 'absolute',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            pointerEvents: 'none',
            userSelect: 'none',
            zIndex: 1000,
          }}>
            {hasFootprintClash && (
              <div className="warning-box" style={{
                width: '400px',
                padding: '10px',
                background: renderingConfig.clash.color,
                border: `2px solid ${renderingConfig.clash.color}`,
                borderRadius: '8px',
                color: '#fff',
                textAlign: 'center',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}>
                <div style={{ fontSize: '0.9em' }}>
                  ⚠️ The design interferes with lighting components.<br />
                  This design can't be printed.
                </div>
              </div>
            )}
            {hasOverhang && (
              <div className="warning-box" style={{
                width: '400px',
                padding: '10px',
                background: renderingConfig.overhang.color,
                border: `2px solid ${renderingConfig.overhang.color}`,
                borderRadius: '8px',
                color: '#fff',
                textAlign: 'center',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}>
                <div style={{ fontSize: '0.9em' }}>
                  ⚠️ Overhang thresholds have been exceeded.<br />
                  This design can't be printed.
                </div>
              </div>
            )}
            {hasPrintableAreaClash && (
              <div className="warning-box" style={{
                width: '400px',
                padding: '10px',
                background: renderingConfig.printableArea.color,
                border: `2px solid ${renderingConfig.printableArea.color}`,
                borderRadius: '8px',
                color: '#000',
                textAlign: 'center',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}>
                <div style={{ fontSize: '0.9em' }}>
                  ⚠️ The design exceeds the maximum printable area.<br />
                  This design can't be printed.
                </div>
              </div>
            )}
          </div>
          <div className="canvas-overlay">
            <button 
              className="orbit-pause-button"
              onClick={() => setIsOrbitPaused(!isOrbitPaused)}
            >
              {isOrbitPaused ? '▶ Resume Orbit' : '⏸ Pause Orbit'}
            </button>
          </div>
          {showCost && (
            <button 
              className="cost-float-field buy-now-button"
              onClick={() => {
                handleAddToCart();
                navigate('/checkout');
              }}
            >
              Buy Now: ${costEstimate.cost.toFixed(2)}
            </button>
          )}
        </div>
        <div className="controls">
          {isAdminMode && (
            <div className="control-group" style={{ backgroundColor: '#2a2a2a', border: '2px solid #f15a1c', marginBottom: '10px' }}>
              <h3 style={{ color: '#f15a1c' }}>ADMIN MODE</h3>
              <div className="control-content">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                  <label style={{ color: '#e0e0e0', fontSize: '14px', fontWeight: 500 }}>
                    <input
                      type="checkbox"
                      checked={editingEnabled}
                      onChange={(e) => setEditingEnabled(e.target.checked)}
                      style={{ marginRight: '8px', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    Enable Editing
                  </label>
                </div>
                <div style={{ fontSize: '12px', color: '#999', fontStyle: 'italic' }}>
                  {editingEnabled ? 'Editing is enabled. All controls are active.' : 'Editing is disabled. Only Developer Options are available.'}
                </div>
              </div>
            </div>
          )}
          {loadingOrderDesign && (
            <div className="control-group" style={{ textAlign: 'center', padding: '20px' }}>
              <div style={{ color: '#e0e0e0' }}>Loading design from order...</div>
            </div>
          )}
          <div className="control-group" style={{ opacity: isAdminMode && !editingEnabled ? 0.5 : 1, pointerEvents: isAdminMode && !editingEnabled ? 'none' : 'auto' }}>
            <h3>SAVE/LOAD</h3>
            <div className="control-content">
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <button 
                  onClick={handleUndo}
                  className="download-stl-button"
                  style={{ 
                    backgroundColor: currentStateIndex > 0 ? '#666' : '#666',
                    cursor: currentStateIndex > 0 ? 'pointer' : 'not-allowed',
                    flex: 1
                  }}
                  disabled={currentStateIndex === 0 || (isAdminMode && !editingEnabled)}
                >
                  Undo
                </button>
                <button 
                  onClick={handleRedo}
                  className="download-stl-button"
                  style={{ 
                    backgroundColor: currentStateIndex < history.length - 1 ? '#f15a1c' : '#666',
                    cursor: currentStateIndex < history.length - 1 ? 'pointer' : 'not-allowed',
                    flex: 1
                  }}
                  disabled={currentStateIndex === history.length - 1 || (isAdminMode && !editingEnabled)}
                >
                  Redo
                </button>
              </div>
              <button 
                onClick={handleNewLamp}
                className="download-stl-button"
                style={{ backgroundColor: '#f15a1c', marginBottom: '10px' }}
                disabled={isAdminMode && !editingEnabled}
              >
                New Lamp
              </button>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <button 
                  className="download-stl-button"
                  onClick={handleSaveLampToDB}
                  style={{ flex: 1 }}
                  disabled={isAdminMode && !editingEnabled}
                >
                  Save Lamp
                </button>
                <button 
                  className="download-stl-button"
                  onClick={handleLoadFromDB}
                  disabled={loadingDesigns || (isAdminMode && !editingEnabled)}
                  style={{ flex: 1 }}
                >
                  {loadingDesigns ? 'Loading...' : 'Load Lamp'}
                </button>
              </div>
              <button 
                onClick={() => handleRandomiseLamp(false)}
                className="download-stl-button"
                style={{ backgroundColor: '#f15a1c' }}
                disabled={isAdminMode && !editingEnabled}
              >
                Randomise Lamp
              </button>
              {showMainAreaFeedbackButtons && (
                <div className="feedback-buttons">
                  <button 
                    className="feedback-button like"
                    onClick={() => handleFeedback(true)}
                  >
                    👍 Like This Design
                  </button>
                  <button 
                    className="feedback-button dislike"
                    onClick={() => handleFeedback(false)}
                  >
                    👎 Dislike This Design
                  </button>
                </div>
              )}
              {feedbackNotification && (
                <div 
                  className={`feedback-notification ${feedbackNotification.type}`}
                  style={{
                    display: 'block',
                    marginTop: '10px',
                    padding: '12px 24px',
                    borderRadius: '4px',
                    backgroundColor: feedbackNotification.type === 'success' ? '#4CAF50' : '#2196F3',
                    color: 'white',
                    textAlign: 'center',
                    width: '100%',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                  }}
                >
                  {feedbackNotification.message}
                </div>
              )}
            </div>
          </div>

          <div className={`control-group ${activeControlGroup === 'basic' ? 'active' : ''} ${collapsedGroups.basic ? 'collapsed' : ''}`} style={{ opacity: isAdminMode && !editingEnabled ? 0.5 : 1, pointerEvents: isAdminMode && !editingEnabled ? 'none' : 'auto' }}>
            <h3 onClick={() => toggleCollapse('basic')}>
              BASIC CONTROLS
              <span className={`collapse-icon ${collapsedGroups.basic ? 'collapsed' : ''}`}>▼</span>
            </h3>
            <div className="control-content">
              <div className="control-row">
                <label>Diameter: {diameter}mm</label>
                <input
                  type="range"
                  min="115"
                  max="220"
                  step="1"
                  value={diameter}
                  onChange={(e) => setDiameter(parseInt(e.target.value))}
                  disabled={isAdminMode && !editingEnabled}
                />
              </div>
              <div className="control-row">
                <label>Height: {height}mm</label>
                <input
                  type="range"
                  min="150"
                  max={MAX_DIMENSION}
                  step="1"
                  value={height}
                  onChange={(e) => setHeight(parseInt(e.target.value))}
                  disabled={isAdminMode && !editingEnabled}
                />
              </div>
            </div>
          </div>

          <div className={`control-group ${activeControlGroup === 'modifiers' ? 'active' : ''} ${collapsedGroups.modifiers ? 'collapsed' : ''}`} style={{ opacity: isAdminMode && !editingEnabled ? 0.5 : 1, pointerEvents: isAdminMode && !editingEnabled ? 'none' : 'auto' }}>
            <h3 onClick={() => toggleCollapse('modifiers')}>
              MODIFIERS
              <button 
                className="tooltip-trigger"
                onClick={(e) => {
                  e.stopPropagation();
                  handleTooltipShow('modifiers', e);
                }}
                onMouseLeave={handleTooltipHide}
              >
                ?
              </button>
              <span className={`collapse-icon ${collapsedGroups.modifiers ? 'collapsed' : ''}`}>▼</span>
            </h3>
            <div className="control-content">
              <ModifierList
                modifiers={modifiers}
                onUpdate={handleUpdateModifier}
                onRemove={handleRemoveModifier}
              />
            </div>
          </div>

          <div className={`control-group ${activeControlGroup === 'profile' ? 'active' : ''} ${collapsedGroups.profile ? 'collapsed' : ''}`} style={{ opacity: isAdminMode && !editingEnabled ? 0.5 : 1, pointerEvents: isAdminMode && !editingEnabled ? 'none' : 'auto' }}>
            <h3 onClick={() => toggleCollapse('profile')}>
              PROFILE EDITOR
              <button 
                className="tooltip-trigger"
                onClick={(e) => {
                  e.stopPropagation();
                  handleTooltipShow('profile', e);
                }}
                onMouseLeave={handleTooltipHide}
              >
                ?
              </button>
              <span className={`collapse-icon ${collapsedGroups.profile ? 'collapsed' : ''}`}>▼</span>
            </h3>
            <div className="control-content">
              <ProfileEditor
                onProfileUpdate={setProfilePoints}
                initialPoints={profilePoints}
                maxHeight={height}
                maxDiameter={diameter}
                tubularSegments={tubularSegments}
                height={400}
                disabled={isAdminMode && !editingEnabled}
              />
            </div>
          </div>

          {(isAdmin || isAdminMode) && (
          <div className={`control-group ${activeControlGroup === 'developer' ? 'active' : ''} ${collapsedGroups.developer ? 'collapsed' : ''}`}>
            <h3 onClick={() => toggleCollapse('developer')}>
              DEVELOPER OPTIONS
              <button 
                className="tooltip-trigger"
                onClick={(e) => {
                  e.stopPropagation();
                  handleTooltipShow('developer', e);
                }}
                onMouseLeave={handleTooltipHide}
              >
                ?
              </button>
              <span className={`collapse-icon ${collapsedGroups.developer ? 'collapsed' : ''}`}>▼</span>
            </h3>
            <div className="control-content">
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px', gap: '10px' }}>
                <label style={{ color: '#ffd166', fontWeight: 500, fontSize: 14 }}>
                  <input
                    type="checkbox"
                    checked={showCost}
                    onChange={e => setShowCost(e.target.checked)}
                    style={{ marginRight: 8 }}
                  />
                  Show Cost
                </label>
              </div>
              {/* Add Export STL button at the top of developer options */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                <button 
                  onClick={handleDownloadSTLLocal}
                  className="download-stl-button"
                  style={{ flex: 1 }}
                >
                  Export STL (Local)
                </button>
                <button 
                  onClick={handleDownloadSTLLambda}
                  className="download-stl-button"
                  style={{ flex: 1 }}
                >
                  Export STL (Lambda)
                </button>
              </div>

              {/* Add local controls */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                <button 
                  onClick={handleSaveLamp}
                  className="download-stl-button"
                  style={{ flex: 1 }}
                >
                  Save Lamp
                </button>
                <div className="file-input-container" style={{ flex: 1 }}>
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleLoadLamp}
                    style={{ display: 'none' }}
                    id="load-lamp-input"
                  />
                  <label htmlFor="load-lamp-input" className="download-stl-button" style={{ width: '100%', textAlign: 'center' }}>
                    Load Lamp
                  </label>
                </div>
              </div>

              {/* Developer Randomize Button */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                <button 
                  onClick={() => handleRandomiseLamp(true)}
                  className="download-stl-button"
                  style={{ 
                    flex: 1, 
                    backgroundColor: '#4a90e2',
                    border: '1px solid #357abd'
                  }}
                >
                  Randomise Lamp (Developer)
                </button>
              </div>

              {/* Developer Feedback Buttons */}
              {showDeveloperFeedbackButtons && (
                <div className="feedback-buttons" style={{ marginBottom: '15px' }}>
                  <button 
                    className="feedback-button like"
                    onClick={() => handleFeedback(true)}
                    style={{ 
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      marginRight: '10px'
                    }}
                  >
                    👍 Like This Design
                  </button>
                  <button 
                    className="feedback-button dislike"
                    onClick={() => handleFeedback(false)}
                    style={{ 
                      backgroundColor: '#f44336',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    👎 Dislike This Design
                  </button>
                </div>
              )}
              {feedbackNotification && (
                <div 
                  className={`feedback-notification ${feedbackNotification.type}`}
                  style={{
                    display: 'block',
                    marginBottom: '15px',
                    padding: '12px 24px',
                    borderRadius: '4px',
                    backgroundColor: feedbackNotification.type === 'success' ? '#4CAF50' : '#2196F3',
                    color: 'white',
                    textAlign: 'center',
                    width: '100%',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                  }}
                >
                  {feedbackNotification.message}
                </div>
              )}

              {/* Add ground plane toggle at the top of developer options */}
              <div className="control-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <label style={{ color: '#e0e0e0', fontSize: '13px', whiteSpace: 'nowrap' }}>Show Ground Plane</label>
                  <div className="toggle-container" style={{ marginLeft: '10px' }}>
                    <input
                      type="checkbox"
                      checked={renderingConfig.view.showGroundPlane}
                      onChange={(e) => updateRenderingConfig('view.showGroundPlane', e.target.checked)}
                    />
                  </div>
                </div>
              </div>

              <div className="control-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <label style={{ color: '#e0e0e0', fontSize: '13px', whiteSpace: 'nowrap' }}>Show Wireframe</label>
                  <div className="toggle-container" style={{ marginLeft: '10px' }}>
                    <input
                      type="checkbox"
                      checked={showWireframe}
                      onChange={(e) => setShowWireframe(e.target.checked)}
                    />
                  </div>
                </div>
              </div>
              {showWireframe && (
                <div className="control-group">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <label style={{ color: '#e0e0e0', fontSize: '13px', whiteSpace: 'nowrap' }}>Wireframe Color (Auto)</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                      <div style={{ 
                        fontSize: '11px',
                        color: '#888',
                        fontStyle: 'italic'
                      }}>
                        Automatically calculated for contrast compliance
                      </div>
                    </div>
                    <div style={{ 
                      width: '20px', 
                      height: '20px', 
                      backgroundColor: `rgb(${Math.round(pickWireframeGray(new THREE.Color(renderingConfig.material.color), MIN_CONTRAST_RATIO).r * 255)},${Math.round(pickWireframeGray(new THREE.Color(renderingConfig.material.color), MIN_CONTRAST_RATIO).g * 255)},${Math.round(pickWireframeGray(new THREE.Color(renderingConfig.material.color), MIN_CONTRAST_RATIO).b * 255)})`,
                      marginLeft: '10px',
                      border: '1px solid #666'
                    }} />
                  </div>
                </div>
              )}
              {showWireframe && (
                <div className="control-group">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <label style={{ color: '#e0e0e0', fontSize: '13px', whiteSpace: 'nowrap' }}>Line Thickness: {renderingConfig.view.wireframeThickness?.toFixed(1) || '1.0'}</label>
                    <input
                      type="range"
                      min="0.1"
                      max="2"
                      step="0.1"
                      value={renderingConfig.view.wireframeThickness || 1}
                      onChange={(e) => updateRenderingConfig('view.wireframeThickness', parseFloat(e.target.value))}
                      style={{ width: '150px' }}
                    />
                  </div>
                </div>
              )}
              <div className="control-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <label style={{ color: '#e0e0e0', fontSize: '13px', whiteSpace: 'nowrap' }}>Show Footprint</label>
                  <div className="toggle-container" style={{ marginLeft: '10px' }}>
                    <input
                      type="checkbox"
                      checked={showFootprint}
                      onChange={(e) => setShowFootprint(e.target.checked)}
                    />
                  </div>
                </div>
              </div>
              <div className="control-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <label style={{ color: '#e0e0e0', fontSize: '13px', whiteSpace: 'nowrap' }}>Show Printable Area Bounds</label>
                  <div className="toggle-container" style={{ marginLeft: '10px' }}>
                    <input
                      type="checkbox"
                      checked={showPrintableAreaBounds}
                      onChange={(e) => setShowPrintableAreaBounds(e.target.checked)}
                    />
                  </div>
                </div>
              </div>
              <div className="control-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <label style={{ color: '#e0e0e0', fontSize: '13px', whiteSpace: 'nowrap' }}>Show Overhang Debug</label>
                  <div className="toggle-container" style={{ marginLeft: '10px' }}>
                    <input
                      type="checkbox"
                      checked={showDebug}
                      onChange={(e) => setShowDebug(e.target.checked)}
                    />
                  </div>
                </div>
              </div>
              <div className="control-row">
                <label style={{ color: '#e0e0e0', fontSize: '13px' }}>Overhang Threshold: {angleThreshold}°</label>
                <input
                  type="range"
                  min="0"
                  max="90"
                  step="1"
                  value={angleThreshold}
                  onChange={(e) => setAngleThreshold(parseInt(e.target.value))}
                />
              </div>

              {/* Video Uploader for Tooltip Videos */}
              <div style={{ marginTop: '20px', padding: '15px', background: '#2a2a2a', borderRadius: '8px' }}>
                {/* Video Uploader component has been removed as per instructions */}
              </div>

              <div className="modifier">
                <div className="modifier-header">
                  <span>Material Settings</span>
                  <div className="modifier-actions">
                    <button 
                      className="tooltip-trigger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTooltipShow('material', e);
                      }}
                      onMouseLeave={handleTooltipHide}
                    >
                      ?
                    </button>
                  </div>
                </div>
                <div className="modifier-parameters">
                  <div className="control-group">
                    <label>Color</label>
                    <input
                      type="color"
                      value={renderingConfig.material.color}
                      onChange={(e) => updateRenderingConfig('material.color', e.target.value)}
                    />
                  </div>
                  <div className="control-group">
                    <label>Roughness: {renderingConfig.material.roughness.toFixed(2)}</label>
                    <input
                      type="range"
                      min="0.5"
                      max="1"
                      step="0.01"
                      value={renderingConfig.material.roughness}
                      onChange={(e) => updateRenderingConfig('material.roughness', parseFloat(e.target.value))}
                    />
                  </div>
                  <div className="control-group">
                    <label>Metalness: {renderingConfig.material.metalness.toFixed(2)}</label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={renderingConfig.material.metalness}
                      onChange={(e) => updateRenderingConfig('material.metalness', parseFloat(e.target.value))}
                    />
                  </div>
                  <div className="control-group">
                    <label>Opacity: {renderingConfig.material.opacity.toFixed(2)}</label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={renderingConfig.material.opacity}
                      onChange={(e) => updateRenderingConfig('material.opacity', parseFloat(e.target.value))}
                    />
                  </div>
                  <div className="control-group">
                    <label>Bump Scale: {renderingConfig.material.bumpScale.toFixed(2)}</label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={renderingConfig.material.bumpScale}
                      onChange={(e) => updateRenderingConfig('material.bumpScale', parseFloat(e.target.value))}
                    />
                  </div>
                  <div className="control-group">
                    <label>Emissive Color</label>
                    <input
                      type="color"
                      value={renderingConfig.material.emissiveColor}
                      onChange={(e) => updateRenderingConfig('material.emissiveColor', e.target.value)}
                    />
                  </div>
                  <div className="control-group">
                    <label>Emissive Intensity: {renderingConfig.material.emissiveIntensity.toFixed(2)}</label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={renderingConfig.material.emissiveIntensity}
                      onChange={(e) => updateRenderingConfig('material.emissiveIntensity', parseFloat(e.target.value))}
                    />
                  </div>
                </div>
              </div>

              <div className="modifier">
                <div className="modifier-header">
                  <span>Lighting Settings</span>
                  <div className="modifier-actions">
                    <button 
                      className="tooltip-trigger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTooltipShow('lighting', e);
                      }}
                      onMouseLeave={handleTooltipHide}
                    >
                      ?
                    </button>
                  </div>
                </div>
                <div className="modifier-parameters">
                  <div className="control-group">
                    <label>Ambient Color</label>
                    <input
                      type="color"
                      value={renderingConfig.lighting.ambientColor}
                      onChange={(e) => updateRenderingConfig('lighting.ambientColor', e.target.value)}
                    />
                  </div>
                  <div className="control-group">
                    <label>Ambient Intensity: {renderingConfig.lighting.ambientIntensity.toFixed(2)}</label>
                    <input
                      type="range"
                      min="0"
                      max="2"
                      step="0.1"
                      value={renderingConfig.lighting.ambientIntensity}
                      onChange={(e) => updateRenderingConfig('lighting.ambientIntensity', parseFloat(e.target.value))}
                    />
                  </div>
                  <div className="control-group">
                    <label>Directional Color</label>
                    <input
                      type="color"
                      value={renderingConfig.lighting.directionalColor}
                      onChange={(e) => updateRenderingConfig('lighting.directionalColor', e.target.value)}
                    />
                  </div>
                  <div className="control-group">
                    <label>Directional Intensity: {renderingConfig.lighting.directionalIntensity.toFixed(2)}</label>
                    <input
                      type="range"
                      min="0"
                      max="2"
                      step="0.1"
                      value={renderingConfig.lighting.directionalIntensity}
                      onChange={(e) => updateRenderingConfig('lighting.directionalIntensity', parseFloat(e.target.value))}
                    />
                  </div>
                  <div className="control-group">
                    <label>Point Light Color</label>
                    <input
                      type="color"
                      value={renderingConfig.lighting.pointLightColor}
                      onChange={(e) => updateRenderingConfig('lighting.pointLightColor', e.target.value)}
                    />
                  </div>
                  <div className="control-group">
                    <label>Point Light Intensity: {renderingConfig.lighting.pointLightIntensity.toFixed(2)}</label>
                    <input
                      type="range"
                      min="0"
                      max="2"
                      step="0.1"
                      value={renderingConfig.lighting.pointLightIntensity}
                      onChange={(e) => updateRenderingConfig('lighting.pointLightIntensity', parseFloat(e.target.value))}
                    />
                  </div>
                </div>
              </div>

              <div className="modifier">
                <div className="modifier-header">
                  <span>Environment Settings</span>
                  <div className="modifier-actions">
                    <button 
                      className="tooltip-trigger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTooltipShow('environment', e);
                      }}
                      onMouseLeave={handleTooltipHide}
                    >
                      ?
                    </button>
                  </div>
                </div>
                <div className="modifier-parameters">
                  <div className="control-group">
                    <label>Background Color</label>
                    <input
                      type="color"
                      value={renderingConfig.background.color}
                      onChange={(e) => updateRenderingConfig('background.color', e.target.value)}
                    />
                  </div>
                  <div className="control-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={shadowsEnabled}
                        onChange={(e) => setShadowsEnabled(e.target.checked)}
                      />
                      Enable Shadows
                    </label>
                  </div>
                  <div className="control-group">
                    <label>Shadow Quality</label>
                    <select
                      value={renderingConfig.shadows.mapSize}
                      onChange={(e) => updateRenderingConfig('shadows.mapSize', parseInt(e.target.value))}
                    >
                      <option value="512">Low</option>
                      <option value="1024">Medium</option>
                      <option value="2048">High</option>
                      <option value="4096">Ultra</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="modifier">
                <div className="modifier-header">
                  <span>Camera Settings</span>
                  <div className="modifier-actions">
                    <button 
                      className="tooltip-trigger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTooltipShow('camera', e);
                      }}
                      onMouseLeave={handleTooltipHide}
                    >
                      ?
                    </button>
                  </div>
                </div>
                <div className="modifier-parameters">
                  <div className="control-group">
                    <label>Camera Distance: {Math.round(cameraDistance)}mm</label>
                    <input
                      type="range"
                      min="400" // Keep minimum distance at 400
                      max="1000"
                      step="10"
                      value={cameraDistance}
                      onChange={(e) => {
                        setCameraDistance(parseInt(e.target.value));
                      }}
                    />
                  </div>
                  <div className="control-group">
                    <button
                      onClick={() => {
                        // Reset camera to original default position
                        setCameraPosition([0, 125, 700]);
                        setCameraTarget([0, 125, 0]);
                        setCameraDistance(700);
                      }}
                      className="download-stl-button"
                      style={{ backgroundColor: '#666', width: '100%' }}
                    >
                      Reset Camera
                    </button>
                  </div>
                </div>
              </div>

              <div className="modifier">
                <div className="modifier-header">
                  <span>Debug Visualization Colors</span>
                  <div className="modifier-actions">
                    <button 
                      className="tooltip-trigger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTooltipShow('debug_colors', e);
                      }}
                      onMouseLeave={handleTooltipHide}
                    >
                      ?
                    </button>
                  </div>
                </div>
                <div className="modifier-parameters">
                  <div className="control-group">
                    <label>Overhang Color</label>
                    <input
                      type="color"
                      value={renderingConfig.overhang.color || '#ff0000'}
                      onChange={(e) => updateRenderingConfig('overhang.color', e.target.value)}
                    />
                  </div>
                  <div className="control-group">
                    <label>Clash Detection Color</label>
                    <input
                      type="color"
                      value={renderingConfig.clash.color || '#0088ff'}
                      onChange={(e) => updateRenderingConfig('clash.color', e.target.value)}
                    />
                  </div>
                  <div className="control-group">
                    <label>Printable Area Color</label>
                    <input
                      type="color"
                      value={renderingConfig.printableArea.color || '#ffd700'}
                      onChange={(e) => updateRenderingConfig('printableArea.color', e.target.value)}
                    />
                  </div>
                  <div className="control-group">
                    <label>Overhang Opacity: {renderingConfig.overhang.opacity.toFixed(2)}</label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={renderingConfig.overhang.opacity}
                      onChange={(e) => updateRenderingConfig('overhang.opacity', parseFloat(e.target.value))}
                    />
                  </div>
                  <div className="control-group">
                    <label>Clash Opacity: {renderingConfig.clash.opacity || 0.5}</label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={renderingConfig.clash.opacity || 0.5}
                      onChange={(e) => updateRenderingConfig('clash.opacity', parseFloat(e.target.value))}
                    />
                  </div>
                  <div className="control-group">
                    <label>Printable Area Opacity: {renderingConfig.printableArea.opacity || 0.5}</label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={renderingConfig.printableArea.opacity || 0.5}
                      onChange={(e) => updateRenderingConfig('printableArea.opacity', parseFloat(e.target.value))}
                    />
                  </div>
                </div>
              </div>

              <div className="modifier">
                <div className="modifier-header">
                  <span>Lighting Offset (relative to camera)</span>
                </div>
                <div className="modifier-parameters">
                  <div className="control-group">
                    <label>X: {lightOffset.x}</label>
                    <input
                      type="range"
                      min={-500}
                      max={500}
                      step={1}
                      value={lightOffset.x}
                      onChange={(e) => handleLightOffsetChange('x', parseInt(e.target.value))}
                    />
                  </div>
                  <div className="control-group">
                    <label>Y: {lightOffset.y}</label>
                    <input
                      type="range"
                      min={-500}
                      max={500}
                      step={1}
                      value={lightOffset.y}
                      onChange={(e) => handleLightOffsetChange('y', parseInt(e.target.value))}
                    />
                  </div>
                  <div className="control-group">
                    <label>Z: {lightOffset.z}</label>
                    <input
                      type="range"
                      min={-500}
                      max={500}
                      step={1}
                      value={lightOffset.z}
                      onChange={(e) => handleLightOffsetChange('z', parseInt(e.target.value))}
                    />
                  </div>
                  <button onClick={handleCopyOffsetCode} style={{ marginTop: '10px' }}>
                    Copy Offset Code
                  </button>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>

      {activeTooltip && (
        <MenuTooltip
          {...MENU_TOOLTIPS[activeTooltip]}
          isVisible={true}
          position={tooltipPosition}
          triggerElement={tooltipTrigger}
        />
      )}

      {/* Design List Modal */}
      {showDesignList && (
        <div className="modal">
          <div className="modal-content" style={{ position: 'relative' }}>
            <button
              onClick={() => setShowDesignList(false)}
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                width: 32,
                height: 32,
                border: 'none',
                borderRadius: '50%',
                background: '#eee',
                color: '#333',
                fontSize: 20,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10
              }}
              aria-label="Close"
            >
              ×
            </button>
            <h2>SAVED DESIGNS</h2>
            <div className="design-list-controls">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={showRatedOnly}
                  onChange={(e) => setShowRatedOnly(e.target.checked)}
                />
                <span className="toggle-slider"></span>
                Show Rated Designs Only
              </label>
            </div>
            {loadingDesigns ? (
              <div>Loading designs...</div>
            ) : (
              <div className="design-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', alignItems: 'flex-start' }}>
                {availableDesigns.filter(design => !showRatedOnly || (design.ratings && design.ratings.score > 0)).length === 0 ? (
                  <div
                    role="status"
                    aria-live="polite"
                    style={{
                      width: '100%',
                      padding: '40px 0',
                      textAlign: 'center',
                      color: '#888',
                      fontSize: '1.2em',
                      fontWeight: 500
                    }}
                  >
                    <span aria-label="No saved lamps">You have no saved lamps yet.</span>
                  </div>
                ) : (
                  availableDesigns
                    .filter(design => !showRatedOnly || (design.ratings && design.ratings.score > 0))
                    .map((design) => (
                      <DesignCard
                        key={design.id}
                        design={design}
                        onEdit={() => handleSelectDesign(design.id)}
                        onDelete={() => handleDeleteDesign(design.id)}
                      />
                    ))
                )}
              </div>
            )}
          </div>
        </div>
      )}
      <ToastContainer position="top-center" autoClose={2500} hideProgressBar={false} newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover />
    </>
  );
}

export default LampBuilder;
